import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";

import { ethers } from "ethers";

import { api } from "./lib/api";
import { API_BASE_URL } from "./config";
import {
  getDeploymentDisplayName,
  getDeploymentAddress,
  getDeploymentTxHash,
  getDeploymentDownload,
  getDeploymentBundleLabel,
  hasDeploymentVerificationContext,
  getCustomVerificationTarget,
  isCustomDeploymentRecord,
} from "./lib/deploymentRecordUi";
import { requestCustomVerification } from "./lib/customContractsApi";
import {
  buildCustomMintArgs,
  confirmCustomTokenAction,
  getCustomActionCapabilities,
  isCustomTokenInfo,
  prepareCustomTokenAction,
} from "./lib/customTokenActions";
import AppLayout from "./components/layout/AppLayout";
import CustomizeSolidityButton from "./components/custom/CustomizeSolidityButton";
import Operations from "./dashboard/Operations";
import Deployments from "./pages/Deployments";
import About from "./pages/About";
import Landing from "./pages/Landing";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import RefundPolicy from "./pages/RefundPolicy";
import Contact from "./pages/Contact";
import Trust from "./pages/Trust";
import Examples from "./pages/Examples";
import Guide from "./pages/Guide";

import History from "./pages/History";
import TokenDashboard from "./pages/TokenDashboard";
import CustomBuilder from "./pages/CustomBuilder";
import HelpCenter from "./pages/HelpCenter";
import TicketDetail from "./pages/TicketDetail";
import { useAuth } from "./auth/useAuth";
import {
  isAppKitConnected,
  getAppKitAddress,
  subscribeToAppKitProvider,
  getCurrentProvider,
  getCurrentSigner,
  getCurrentAddress,
  resolveWalletConnectSession,
  switchActiveWalletChain,
  sendActiveWalletTransaction,
} from "./wallet/walletClient";
import { AuthProvider } from "./auth/AuthContext";
import Upgrade from "./pages/Upgrade";
import {
  hasPaidFeatureAccess,
  normalizePlanCode,
} from "./components/billing/plans";
import AdminDashboard from "./pages/AdminDashboard";
import AccountSettings from "./pages/AccountSettings";
import UpgradePrompt from "./components/billing/UpgradePrompt";
import { NetworkStatusProvider } from "./context/NetworkStatusContext";
import GlobalNetworkBanner from "./components/system/GlobalNetworkBanner";
import PaddleCheckoutHandler from "./components/PaddleCheckoutHandler";

/**
 * ---------------------------------------------------------------------
 * ChainForge Frontend (User Pays Gas)
 * ---------------------------------------------------------------------
 *
 * Architecture:
 * - Backend PREPARES unsigned tx (deterministic calldata / initcode)
 * - Wallet SIGNS + BROADCASTS (user pays gas)
 * - Backend CONFIRMS (receipt parsing + optional verification + history record)
 *
 * UX principles:
 * - Single source of truth for auth + wallet controls: <AuthBar /> (global header)
 * - Studio/Home NEVER duplicates connect/sign-in buttons
* - Studio/Home still reads walletAddress from the active wallet once connected
* - Defensive wallet request locking to avoid -32002 pending request errors
 */

/* ---------------------------------------------------------------------
 * Small utilities
 * ------------------------------------------------------------------- */

function isNonEmptyString(v) {
  return typeof v === "string" && v.trim().length > 0;
}

function isEvmAddress(v) {
  return typeof v === "string" && /^0x[a-fA-F0-9]{40}$/.test(v.trim());
}

function normalizeTokenType(v) {
  const t = String(v || "")
    .trim()
    .toUpperCase();
  if (t === "ERC20" || t === "ERC721" || t === "ERC1155") return t;
  return null;
}

function shorten(value) {
  if (!value || typeof value !== "string") return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function formatSupplyDisplay(value, decimals = null, rawValue = null) {
  const hasValue = value != null && String(value).trim() !== "";
  if (hasValue) {
    const raw = String(value).trim();
    const num = Number(raw);
    if (Number.isFinite(num)) return num.toLocaleString();
    return raw;
  }


  const hasRaw = rawValue != null && String(rawValue).trim() !== "";
  if (hasRaw && decimals != null && String(decimals).trim() !== "") {
    try {
      const formatted = ethers.formatUnits(String(rawValue), Number(decimals));
      const num = Number(formatted);
      return Number.isFinite(num) ? num.toLocaleString() : formatted;
    } catch {
      return String(rawValue);
    }
  }

  return "-";
}

function formatPreflightSupply(value) {
  const raw = String(value ?? "").trim();

  if (!raw) return "-";

  if (raw.includes(".")) {
    return raw.replace(/\.?0+$/, "") || "0";
  }

  return raw;
}

function safeApiError(err, fallback = "Something went wrong.") {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function humanizeWalletActionError(err, fallback = "Transaction failed.") {
  const code = err?.code;
  const nestedCode = err?.info?.error?.code;
  const message = String(
    err?.shortMessage ||
    err?.reason ||
    err?.message ||
    err?.info?.error?.message ||
    "",
  ).toLowerCase();

  const isRejected =
    code === "ACTION_REJECTED" ||
    code === 4001 ||
    nestedCode === 4001 ||
    nestedCode === 5000 ||
    message.includes("user denied") ||
    message.includes("user rejected") ||
    message.includes("rejected") ||
    message.includes("denied transaction signature");

  if (isRejected) {
    return "Mint was cancelled in your wallet.";
  }

  return safeApiError(err, fallback);
}

const PENDING_DEPLOY_KEY = "chainforge_pending_deploy";

function savePendingDeploy(data) {
  try {
    localStorage.setItem(PENDING_DEPLOY_KEY, JSON.stringify(data));
  } catch { }
}

function getPendingDeploy() {
  try {
    return JSON.parse(localStorage.getItem(PENDING_DEPLOY_KEY));
  } catch {
    return null;
  }
}

function clearPendingDeploy() {
  try {
    localStorage.removeItem(PENDING_DEPLOY_KEY);
  } catch { }
}

function extractDeployedAddress(obj) {
  return (
    obj?.address ||
    obj?.contractAddress ||
    obj?.result?.address ||
    obj?.result?.contractAddress ||
    null
  );
}

function isIntegerString(v) {
  if (!isNonEmptyString(v)) return false;
  return /^[0-9]+$/.test(v.trim());
}

function isHexBytes(v) {
  if (!isNonEmptyString(v)) return false;
  const s = v.trim();
  return /^0x([a-fA-F0-9]{2})*$/.test(s);
}

/* ---------------------------------------------------------------------
 * Home (Studio) — Deploy + Actions
 * ------------------------------------------------------------------- */
const DEFAULT_ERC721_BASE_URI = "ipfs://YOUR_METADATA_CID/";
const DEFAULT_ERC1155_BASE_URI = "ipfs://YOUR_METADATA_CID/{id}.json";

const NETWORK_META = {
  ethereum: {
    logo: "/logos/ethereum.png",
    tone: "from-slate-100 to-slate-50",
  },
  sepolia: {
    logo: "/logos/ethereum.png",
    tone: "from-indigo-50 to-sky-50",
  },
  polygon: {
    logo: "/logos/polygon.png",
    tone: "from-purple-50 to-indigo-50",
  },
  arbitrum: {
    logo: "/logos/arbitrum.png",
    tone: "from-sky-50 to-blue-50",
  },
  avalanche: {
    logo: "/logos/avalanche.png",
    tone: "from-rose-50 to-red-50",
  },
  bnb: {
    logo: "/logos/bnb.png",
    tone: "from-amber-50 to-yellow-50",
  },
  base: {
    logo: "/logos/base.png",
    tone: "from-blue-50 to-cyan-50",
  },
  optimism: {
    logo: "/logos/optimism.png",
    tone: "from-rose-50 to-orange-50",
  },
};

function getNetworkMeta(network) {
  const key = String(network?.key || network?.label || "")
    .trim()
    .toLowerCase();

  if (key.includes("arbitrum")) return NETWORK_META.arbitrum;
  if (key.includes("avalanche")) return NETWORK_META.avalanche;
  if (key.includes("polygon") || key.includes("amoy")) return NETWORK_META.polygon;
  if (key.includes("bnb") || key.includes("bsc")) return NETWORK_META.bnb;
  if (key.includes("base")) return NETWORK_META.base;
  if (key.includes("optimism") || key.includes("op ")) return NETWORK_META.optimism;
  if (key.includes("ethereum")) return NETWORK_META.ethereum;
  if (key.includes("sepolia")) return NETWORK_META.sepolia;

  return {
    logo: "",
    tone: "from-slate-50 to-white",
  };
}

function isTestnetNetwork(network) {
  const value = String(network?.label || network?.key || "")
    .trim()
    .toLowerCase();

  return (
    value.includes("testnet") ||
    value.includes("sepolia") ||
    value.includes("fuji")
  );
}

function getNetworkShortName(network) {
  const label = String(network?.label || network?.key || "")
    .trim()
    .toUpperCase();

  if (label.includes("ETHEREUM")) return "ETH";
  if (label.includes("ARBITRUM")) return "ARB";
  if (label.includes("AVALANCHE")) return "AVAX";
  if (label.includes("POLYGON")) return "POL";
  if (label.includes("BNB")) return "BNB";
  if (label.includes("BASE")) return "BASE";
  if (label.includes("OP")) return "OP";

  return label.slice(0, 3) || "NET";
}

function Home() {
  const location = useLocation();


  /**
   * NOTE:
   * - AuthBar handles Connect + Sign-in in the global header.
   * - Home reads wallet address passively (eth_accounts + events).
   */
  const { isAuthed, restoreLoading, authReady = true, session } = useAuth();
  const sessionAddress = String(session?.address || "").trim();
  const [accountUsage, setAccountUsage] = useState(null);
  const [accountUsageLoading, setAccountUsageLoading] = useState(false);
  const [accountUsageError, setAccountUsageError] = useState("");

  /* ---------------- Deploy Form ---------------- */
  const [tokenType, setTokenType] = useState("ERC20");
  const [tokenName, setTokenName] = useState("");
  const [tokenSymbol, setTokenSymbol] = useState("");
  const [erc20Decimals, setErc20Decimals] = useState("18");
  const [erc20InitialSupply, setErc20InitialSupply] = useState("");
  const [baseURI, setBaseURI] = useState("");
  const [royaltyFeeNumerator, setRoyaltyFeeNumerator] = useState("0");
  const [royaltyReceiver, setRoyaltyReceiver] = useState("");
  const [modules, setModules] = useState({
    mintable: false,
    burnable: false,
    pausable: false,
  });

  /* ---------------- Networks ---------------- */
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [deploySelectedNetworks, setDeploySelectedNetworks] = useState([]);
  const [includeTestnets, setIncludeTestnets] = useState(true);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [networksError, setNetworksError] = useState("");
  const [networkPickerOpen, setNetworkPickerOpen] = useState(false);
  const [networkPickerTab, setNetworkPickerTab] = useState("mainnets");

  /* ---------------- Wallet ---------------- */
  const [walletAddress, setWalletAddress] = useState(null);
  const [walletError, setWalletError] = useState("");

  /* ---------------- Async State ---------------- */
  const [deployLoading, setDeployLoading] = useState(false);
  const [mintLoading, setMintLoading] = useState(false);
  const [preflightLoading, setPreflightLoading] = useState(false);

  /* ---------------- Results ---------------- */
  const [statusText, setStatusText] = useState("");
  const [deployResult, setDeployResult] = useState(null);
  const [preflightResult, setPreflightResult] = useState(null);

  const [mintStatusText, setMintStatusText] = useState("");
  const [mintResult, setMintResult] = useState(null);

  const [activityLog, setActivityLog] = useState([]);

  /* ---------------- Recent Deployments ---------------- */
  const [recentDeployments, setRecentDeployments] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState("");
  const [recentPrivacyMode, setRecentPrivacyMode] = useState(true);
  const [verificationSyncing, setVerificationSyncing] = useState({});
  const [verificationSyncMessage, setVerificationSyncMessage] = useState("");

  /* ---------------- Mint Form ---------------- */
  const [mintTokenType, setMintTokenType] = useState("ERC721");
  const [mintContractAddress, setMintContractAddress] = useState("");
  const [mintTo, setMintTo] = useState("");
  const [mintTokenId, setMintTokenId] = useState("");
  const [mintAmount, setMintAmount] = useState("");
  const [mintDataBytes, setMintDataBytes] = useState("0x");

  /* ---------------- Mint Detection ---------------- */
  const [detectedTokenType, setDetectedTokenType] = useState(null);
  const [detectStatus, setDetectStatus] = useState("idle");
  const [detectMessage, setDetectMessage] = useState("");
  const [autoDetectEnabled, setAutoDetectEnabled] = useState(true);
  const detectReqIdRef = useRef(0);

  /* ---------------- Token Info + Balance ---------------- */
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenInfoLoading, setTokenInfoLoading] = useState(false);
  const [tokenInfoError, setTokenInfoError] = useState("");

  const [userBalance, setUserBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState("");

  /* ---------------- Import Existing Token ---------------- */
  const [importAddress, setImportAddress] = useState("");
  const [importStatus, setImportStatus] = useState("");
  const [importedTokens, setImportedTokens] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem("chainforge_imported_tokens") || "[]",
      );
    } catch {
      return [];
    }
  });

  /* -------------------------------------------------------------------
   * Core helpers
   * ----------------------------------------------------------------- */
  const getActiveWalletContext = useCallback(async () => {
    const currentSigner = getCurrentSigner();
    const currentProvider = getCurrentProvider();
    const currentWalletAddress = String(getCurrentAddress() || "").trim();

    if (currentSigner && currentProvider && currentWalletAddress) {
      return {
        signer: currentSigner,
        provider: currentProvider,
        address: currentWalletAddress,
      };
    }

    // Rehydrate mobile WalletConnect / Reown session after refresh
    const appKitAddress = String(getAppKitAddress() || "").trim();
    const appKitActive = isAppKitConnected() && isEvmAddress(appKitAddress);

    if (appKitActive) {
      const wallet = await resolveWalletConnectSession();

      return {
        signer: wallet.signer,
        provider: wallet.provider,
        address: String(wallet.address || "").trim(),
      };
    }

    if (window.ethereum) {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const address = String(await signer.getAddress()).trim();

      return {
        signer,
        provider,
        address,
      };
    }

    throw new Error("No active wallet provider available.");
  }, []);

  const toggleDeployNetwork = useCallback((networkKey) => {
    setDeploySelectedNetworks((prev) =>
      prev.includes(networkKey)
        ? prev.filter((key) => key !== networkKey)
        : [...prev, networkKey],
    );
  }, []);

  const selectedNetworkObj = useMemo(() => {
    return networks.find((n) => n.key === selectedNetwork) || null;
  }, [networks, selectedNetwork]);

  const deploySelectedNetworkObjs = useMemo(() => {
    return networks.filter((n) => deploySelectedNetworks.includes(n.key));
  }, [networks, deploySelectedNetworks]);

  const mainnetNetworks = useMemo(() => {
    return networks.filter((n) => !isTestnetNetwork(n));
  }, [networks]);

  const testnetNetworks = useMemo(() => {
    return networks.filter((n) => isTestnetNetwork(n));
  }, [networks]);

  const selectedModules = useMemo(() => {
    return Object.keys(modules).filter((m) => modules[m]);
  }, [modules]);

  const mintUiTokenType = detectedTokenType || mintTokenType;

  const mintNeedsTokenId =
    mintUiTokenType === "ERC721" || mintUiTokenType === "ERC1155";
  const mintNeedsAmount =
    mintUiTokenType === "ERC20" || mintUiTokenType === "ERC1155";
  const mintNeedsBytes = mintUiTokenType === "ERC1155";

  const artifactKeyForToken = useCallback((type, mods) => {
    const normalized = [...(mods || [])]
      .map((m) => String(m).trim().toLowerCase())
      .filter(Boolean)
      .sort();

    const suffix = normalized.length ? normalized.join("_") : "base";
    return `${type}__${suffix}`;
  }, []);

  const pushActivity = useCallback((type, label) => {
    setActivityLog((prev) => [
      { type, label, timestamp: new Date().toISOString() },
      ...prev,
    ]);
  }, []);

  const loadAccountUsage = useCallback(async () => {
    if (restoreLoading) {
      return;
    }

    if (!isAuthed) {
      setAccountUsage(null);
      setAccountUsageError("");
      return;
    }

    setAccountUsageLoading(true);
    setAccountUsageError("");

    try {
      const res = await api.get("/account/usage", {
        headers: { "Cache-Control": "no-store" },
      });
      setAccountUsage(res.data || null);
    } catch (err) {
      setAccountUsage(null);
      setAccountUsageError(safeApiError(err, "Failed to load account usage."));
    } finally {
      setAccountUsageLoading(false);
    }
  }, [isAuthed, restoreLoading]);

  /* -------------------------------------------------------------------
   * Wallet request lock (prevents -32002 / pending request collisions)
   * ----------------------------------------------------------------- */


  const ensureWalletOnChain = useCallback(async (chainIdNumber) => {
    try {
      await switchActiveWalletChain(chainIdNumber);
    } catch (err) {
      if (err?.code === 4902) {
        throw new Error(
          "This network is not added in the connected wallet. Add it and try again.",
        );
      }
      if (err?.code === -32002) {
        throw new Error(
          "The wallet has a pending request. Open the wallet and complete it.",
        );
      }
      throw err;
    }
  }, []);

  const sendUserTransaction = useCallback(
    async (tx, from, chainId) => {
      return sendActiveWalletTransaction(tx, {
        chainId,
        expectedAddress: from,
      });
    },
    [],
  );

  const sleep = useCallback((ms) => new Promise((r) => setTimeout(r, ms)), []);

  const confirmWithRetry = useCallback(
    async (fn, { attempts = 6, delayMs = 2000 } = {}) => {
      let lastErr = null;

      for (let i = 0; i < attempts; i++) {
        try {
          return await fn();
        } catch (err) {
          lastErr = err;
          if (i === attempts - 1) throw err;
          await sleep(delayMs);
        }
      }

      throw lastErr || new Error("Confirm failed.");
    },
    [sleep],
  );

  /* -------------------------------------------------------------------
   * Wallet sync (passive) — reads wallet after AuthBar connects it
   * ----------------------------------------------------------------- */

  useEffect(() => {
    async function recoverPending() {
      const pending = getPendingDeploy();
      if (!pending) return;

      try {
        console.log("Recovering pending deployment...");

        await api.post("/deployment/confirm", pending, {
          timeout: 120000,
        });

        clearPendingDeploy();
        setStatusText("Recovered previous deployment ✅");
      } catch (err) {
        console.log("Recovery failed, will retry later");
      }
    }

    if (isAuthed && authReady && !restoreLoading) {
      recoverPending();
    }
  }, [isAuthed, authReady, restoreLoading]);

  useEffect(() => {
    let mounted = true;

    async function syncWalletAddress() {
      try {
        let addr = null;

        // 1) Preferred: live Reown/AppKit wallet connection.
        const appKitAddr = String(getAppKitAddress() || "").trim();
        if (isAppKitConnected() && isEvmAddress(appKitAddr)) {
          addr = appKitAddr;
        }

        // 2) Wallet client restored address, if available.
        if (!addr) {
          const currentAddr = String(getCurrentAddress() || "").trim();
          if (isEvmAddress(currentAddr)) {
            addr = currentAddr;
          }
        }

        // 3) Browser extension passive read. This should not open wallet popup.
        if (!addr && window.ethereum?.request) {
          try {
            const accounts = await window.ethereum.request({
              method: "eth_accounts",
            });

            const injectedAddr = String(accounts?.[0] || "").trim();
            if (isEvmAddress(injectedAddr)) {
              addr = injectedAddr;
            }
          } catch {
            // ignore passive wallet read errors
          }
        }

        // 4) Auth session fallback so Studio does not look disconnected
        // while AppKit is restoring after refresh.
        if (!addr && isAuthed && isEvmAddress(sessionAddress)) {
          addr = sessionAddress;
        }

        // 5) Same address stored by AuthBar/useAuth after login.
        if (!addr && isAuthed) {
          try {
            const storedAddr = String(
              localStorage.getItem("chainforge_wallet_address") || "",
            ).trim();

            if (isEvmAddress(storedAddr)) {
              addr = storedAddr;
            }
          } catch {
            // ignore storage errors
          }
        }

        if (!mounted) return;

        setWalletAddress(addr || null);
        setWalletError("");

        if (addr) {
          setMintTo((prev) => prev || addr);
        }
      } catch {
        if (!mounted) return;
        setWalletAddress(null);
      }
    }

    syncWalletAddress();

    const onWalletChanged = () => {
      syncWalletAddress();
    };

    const unsubscribeAppKit = subscribeToAppKitProvider(() => {
      syncWalletAddress();
    });

    window.addEventListener("chainforge:wallet-changed", onWalletChanged);
    window.addEventListener("focus", onWalletChanged);

    return () => {
      mounted = false;
      window.removeEventListener("chainforge:wallet-changed", onWalletChanged);
      window.removeEventListener("focus", onWalletChanged);

      try {
        unsubscribeAppKit?.();
      } catch {
        // ignore cleanup errors
      }
    };
  }, [isAuthed, sessionAddress]);

  /* -------------------------------------------------------------------
   * Studio Prefill (from TokenDashboard deep-link)
   * /?network=...&address=0x...
   * ----------------------------------------------------------------- */

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qNetwork = params.get("network");
    const qAddress = params.get("address");

    if (qNetwork && typeof qNetwork === "string") {
      setSelectedNetwork(qNetwork.trim().toLowerCase());
    }

    if (qAddress && typeof qAddress === "string") {
      const cleaned = qAddress.trim();
      setMintContractAddress(cleaned);
      setAutoDetectEnabled(true);

      // Reset transient UI state for a clean entry
      setMintResult(null);
      setMintStatusText("");
      setStatusText("");
    }
  }, [location.search]);

  useEffect(() => {
    if (isAuthed) return;

    setImportStatus("");
    setImportedTokens([]);
    setImportAddress("");
    setMintContractAddress("");
    setMintResult(null);
    setMintStatusText("");
    setTokenInfo(null);
    setTokenInfoError("");
    setDetectedTokenType(null);
    setMintTokenType("ERC721");
    setMintTo("");
    setMintTokenId("");
    setMintAmount("");
    setMintDataBytes("0x");
    setUserBalance(null);
    setBalanceError("");

    try {
      localStorage.removeItem("chainforge_imported_tokens");
    } catch {
      // ignore
    }
  }, [isAuthed]);

  /* -------------------------------------------------------------------
   * Keep ERC20 decimals sane
   * ----------------------------------------------------------------- */

  useEffect(() => {
    if (tokenType !== "ERC20") {
      setErc20Decimals("18");
      setErc20InitialSupply("");
    }

    if (tokenType === "ERC20") {
      setRoyaltyFeeNumerator("0");
      setRoyaltyReceiver("");
    }

    if (tokenType === "ERC721") {
      setBaseURI((prev) => {
        if (!prev || !prev.trim()) return DEFAULT_ERC721_BASE_URI;
        if (prev === DEFAULT_ERC1155_BASE_URI) return DEFAULT_ERC721_BASE_URI;
        return prev;
      });
    }

    if (tokenType === "ERC1155") {
      setBaseURI((prev) => {
        if (!prev || !prev.trim()) return DEFAULT_ERC1155_BASE_URI;
        if (prev === DEFAULT_ERC721_BASE_URI) return DEFAULT_ERC1155_BASE_URI;
        return prev;
      });
    }
  }, [tokenType]);

  /* -------------------------------------------------------------------
   * Load networks from backend
   * ----------------------------------------------------------------- */

  useEffect(() => {
    let mounted = true;

    async function loadNetworks() {
      setNetworksLoading(true);
      setNetworksError("");

      try {
        const res = await api.get("/networks", {
          params: { includeTestnets, onlyConfigured: true },
        });

        const list = res.data?.networks || [];
        if (!Array.isArray(list) || list.length === 0) {
          throw new Error("No networks returned from backend.");
        }

        if (!mounted) return;

        setNetworks(list);

        if (!list.some((n) => n.key === selectedNetwork)) {
          setSelectedNetwork(list[0].key);
        }

        setDeploySelectedNetworks((prev) => {
          const valid = prev.filter((key) => list.some((n) => n.key === key));
          if (valid.length > 0) return valid;

          return [];
        });
      } catch (err) {
        if (!mounted) return;
        setNetworksError(safeApiError(err, "Failed to load networks."));
      } finally {
        if (mounted) setNetworksLoading(false);
      }
    }

    loadNetworks();

    return () => {
      mounted = false;
    };
  }, [includeTestnets, selectedNetwork]);

  /* -------------------------------------------------------------------
   * Recent Deployments (SaaS: mine=true)
   * - No sign-in button here (AuthBar is the single auth entry point)
   * ----------------------------------------------------------------- */

  const loadRecentDeployments = useCallback(async () => {
    if (restoreLoading) {
      return;
    }

    setRecentLoading(true);
    setRecentError("");

    try {
      if (!isAuthed) {
        setRecentDeployments([]);
        setRecentError("Sign in to view your recent deployments.");
        return;
      }

      const res = await api.get("/deployments", {
        params: { kind: "deployment", mine: true },
      });

      const records = Array.isArray(res?.data?.records) ? res.data.records : [];
      setRecentDeployments(records.slice(0, 5));
    } catch (err) {
      console.error("[recent-deployments] load failed:", err);
      setRecentError("Unable to load recent deployments.");
      setRecentDeployments([]);
    } finally {
      setRecentLoading(false);
    }
  }, [isAuthed, restoreLoading]);

  const refreshRecentDeploymentsUntilSettled = useCallback(async () => {
    const delays = [0, 6000, 15000, 30000];

    for (const delay of delays) {
      if (delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      await loadRecentDeployments();
    }
  }, [loadRecentDeployments]);

  const syncDeploymentVerification = useCallback(
    async (row) => {
      const txHash = String(row?.txHash || "").trim();
      const requestId = String(row?.requestId || "").trim();
      const deploymentFile = String(row?.deploymentFile || "").trim();
      const contractAddress = String(row?.address || row?.contractAddress || "").trim();
      const key = txHash || requestId || deploymentFile || contractAddress;

      if (!txHash && !requestId && !deploymentFile) {
        setVerificationSyncMessage(
          "Unable to sync verification: missing transaction/request reference.",
        );
        return;
      }

      setVerificationSyncMessage("");
      setVerificationSyncing((prev) => ({ ...prev, [key]: true }));

      try {
        if (String(row?.sourceMode || "").trim().toLowerCase() === "custom") {
          await requestCustomVerification(getCustomVerificationTarget(row));
        } else {
          const payload = {
            network: row.network,
            contractAddress: row.contractAddress || row.address,
            txHash: row.txHash || null,
            requestId: row.requestId || null,
          };

          const syncRes = await api.post("/verification/sync-status", payload);

          if (!syncRes?.data?.synced) {
            await api.post("/verify-contract", {
              txHash: payload.txHash,
              requestId: payload.requestId,
              deploymentFile: row.deploymentFile || null,
              allowFailedRetry: true,
            });
          }
        }

        setVerificationSyncMessage(
          String(row?.sourceMode || "").trim().toLowerCase() === "custom"
            ? "Custom verification requested. Refreshing status…"
            : "Verification sync requested. Refreshing status…",
        );

        const delays = [0, 10000, 25000, 45000];

        for (const delay of delays) {
          if (delay > 0) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }

          await loadRecentDeployments();
        }
      } catch (err) {
        console.error("[verification-sync] failed:", err);
        setVerificationSyncMessage(
          safeApiError(err, "Unable to sync verification right now."),
        );
      } finally {
        setVerificationSyncing((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    },
    [loadRecentDeployments],
  );

  useEffect(() => {
    loadRecentDeployments();
  }, [loadRecentDeployments]);

  /* -------------------------------------------------------------------
   * Token type detection from backend
   * ----------------------------------------------------------------- */

  const detectTokenTypeFromBackend = useCallback(
    async ({ network, address }) => {
      const res = await api.get(`/token/${address}`, { params: { network } });
      const data = res.data || {};

      // 1) Best source for ChainForge deployments
      const fromDeployment =
        normalizeTokenType(data?.deployment?.tokenType) ||
        normalizeTokenType(data?.deployment?.standard) ||
        normalizeTokenType(data?.deployment?.type);

      if (fromDeployment) return { tokenType: fromDeployment, raw: data };

      // 2) Direct hints
      const direct =
        normalizeTokenType(data.tokenType) ||
        normalizeTokenType(data.standard) ||
        normalizeTokenType(data.type);

      if (direct) return { tokenType: direct, raw: data };

      // 3) Heuristics
      if (data.isERC1155 === true) return { tokenType: "ERC1155", raw: data };
      if (isNonEmptyString(data.uri) || isNonEmptyString(data.baseURI)) {
        return { tokenType: "ERC1155", raw: data };
      }
      if (data.decimals != null && String(data.decimals).trim() !== "") {
        return { tokenType: "ERC20", raw: data };
      }
      if (isNonEmptyString(data.name) && isNonEmptyString(data.symbol)) {
        return { tokenType: "ERC721", raw: data };
      }

      return { tokenType: null, raw: data };
    },
    [],
  );

  useEffect(() => {
    if (!autoDetectEnabled) return;

    const addr = mintContractAddress.trim();
    const net = selectedNetwork;

    if (!addr) {
      setDetectedTokenType(null);
      setDetectStatus("idle");
      setDetectMessage("");
      return;
    }

    if (!isEvmAddress(addr)) {
      setDetectedTokenType(null);
      setDetectStatus("failed");
      setDetectMessage(
        "Enter a valid contract address (0x...) to auto-detect token type.",
      );
      return;
    }

    const reqId = ++detectReqIdRef.current;
    setDetectStatus("detecting");
    setDetectMessage("");

    const t = setTimeout(async () => {
      try {
        const { tokenType: ttype } = await detectTokenTypeFromBackend({
          network: net,
          address: addr,
        });

        if (reqId !== detectReqIdRef.current) return;

        if (ttype) {
          setDetectedTokenType(ttype);
          setMintTokenType(ttype);
          setDetectStatus("ok");
          setDetectMessage(`Detected: ${ttype}`);
        } else {
          setDetectedTokenType(null);
          setDetectStatus("failed");
          setDetectMessage(
            "Couldn’t detect token type for this address on the selected network. Choose token type manually.",
          );
        }
      } catch (err) {
        if (reqId !== detectReqIdRef.current) return;
        setDetectedTokenType(null);
        setDetectStatus("failed");
        setDetectMessage(safeApiError(err, "Detection failed."));
      }
    }, 450);

    return () => clearTimeout(t);
  }, [
    mintContractAddress,
    selectedNetwork,
    autoDetectEnabled,
    detectTokenTypeFromBackend,
  ]);

  /* -------------------------------------------------------------------
   * Fetch token info from backend
   * ----------------------------------------------------------------- */

  useEffect(() => {
    const addr = mintContractAddress.trim();

    if (!isEvmAddress(addr)) {
      setTokenInfo(null);
      setTokenInfoError("");
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setTokenInfoLoading(true);
        setTokenInfoError("");

        const res = await api.get(`/token/${addr}`, {
          params: { network: selectedNetwork, viewer: walletAddress || "" },
        });

        if (!mounted) return;
        setTokenInfo(res.data || null);
      } catch (err) {
        if (!mounted) return;
        setTokenInfo(null);
        setTokenInfoError(safeApiError(err, "Failed to load token info."));
      } finally {
        if (mounted) setTokenInfoLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [mintContractAddress, selectedNetwork, walletAddress]);

  /* -------------------------------------------------------------------
   * Balance reader (eth_call via ethers)
   * ----------------------------------------------------------------- */

  const refreshUserBalance = useCallback(async () => {
    setBalanceError("");
    setUserBalance(null);

    if (!walletAddress) {
      setBalanceError("Connect wallet in the header.");
      return;
    }
    if (!isEvmAddress(mintContractAddress)) {
      setBalanceError("Enter a valid contract address.");
      return;
    }

    setBalanceLoading(true);

    try {
      const { provider } = await getActiveWalletContext();
      const tokenAddr = mintContractAddress.trim();

      if (!isEvmAddress(mintContractAddress)) {
        setBalanceError("Enter a valid contract address.");
        return;
      }

      setBalanceLoading(true);


      if (mintUiTokenType === "ERC20") {
        const abi = [
          "function balanceOf(address) view returns (uint256)",
          "function decimals() view returns (uint8)",
        ];
        const c = new ethers.Contract(tokenAddr, abi, provider);
        const [bal, dec] = await Promise.all([
          c.balanceOf(walletAddress),
          c.decimals(),
        ]);
        setUserBalance(ethers.formatUnits(bal, dec));
        return;
      }

      if (mintUiTokenType === "ERC721") {
        const abi = ["function balanceOf(address) view returns (uint256)"];
        const c = new ethers.Contract(tokenAddr, abi, provider);
        const bal = await c.balanceOf(walletAddress);
        setUserBalance(bal.toString());
        return;
      }

      if (mintUiTokenType === "ERC1155") {
        if (!isIntegerString(mintTokenId)) {
          setUserBalance("Enter a Token ID to view your ERC1155 balance.");
          return;
        }
        const abi = [
          "function balanceOf(address,uint256) view returns (uint256)",
        ];
        const c = new ethers.Contract(tokenAddr, abi, provider);
        const id = ethers.toBigInt(mintTokenId.trim());
        const bal = await c.balanceOf(walletAddress, id);
        setUserBalance(bal.toString());
        return;
      }

      setBalanceError("Unknown token type.");
    } catch (err) {
      setBalanceError(safeApiError(err, "Failed to fetch balance."));
    } finally {
      setBalanceLoading(false);
    }
  }, [walletAddress, mintContractAddress, mintUiTokenType, mintTokenId, getActiveWalletContext]);

  useEffect(() => {
    if (!walletAddress) return;
    if (!isEvmAddress(mintContractAddress)) return;
    if (detectStatus === "detecting") return;
    if (tokenInfoLoading) return;
    if (mintUiTokenType === "ERC1155" && !isIntegerString(mintTokenId)) return;

    refreshUserBalance();
  }, [
    walletAddress,
    mintContractAddress,
    mintUiTokenType,
    mintTokenId,
    detectStatus,
    tokenInfoLoading,
    refreshUserBalance,
  ]);

  /* -------------------------------------------------------------------
   * Import helpers
   * ----------------------------------------------------------------- */

  const saveImportedTokens = useCallback((list) => {
    setImportedTokens(list);
    try {
      localStorage.setItem("chainforge_imported_tokens", JSON.stringify(list));
    } catch {
      // ignore
    }
  }, []);

  const handleImportToken = useCallback(async () => {
    const addr = importAddress.trim();
    setImportStatus("");

    if (!isAuthed) {
      setImportStatus("Sign in to import tokens.");
      return;
    }

    if (!isEvmAddress(addr)) {
      setImportStatus("Enter a valid contract address (0x...).");
      return;
    }

    try {
      setImportStatus("Importing token…");

      const res = await api.get(`/token/${addr}`, {
        params: { network: selectedNetwork, viewer: walletAddress || "" },
      });

      const data = res.data || {};

      const { tokenType: detected } = await detectTokenTypeFromBackend({
        network: selectedNetwork,
        address: addr,
      });

      if (!detected) {
        setImportStatus(
          "Couldn’t detect token type. You can still select it manually below.",
        );
        setMintContractAddress(addr);
        return;
      }

      setMintContractAddress(addr);
      setDetectedTokenType(detected);
      setMintTokenType(detected);
      setAutoDetectEnabled(true);

      if (walletAddress) setMintTo(walletAddress);

      setTokenInfo(data);

      const entry = {
        network: selectedNetwork,
        address: addr,
        tokenType: detected,
        name: String(
          data?.name || data?.deployment?.token?.tokenName || "",
        ).trim(),
        symbol: String(
          data?.symbol || data?.deployment?.token?.tokenSymbol || "",
        ).trim(),
        explorerUrl: String(data?.explorerUrl || "").trim(),
        importedAt: new Date().toISOString(),
      };

      const key = `${selectedNetwork}:${addr.toLowerCase()}`;
      const existing = Array.isArray(importedTokens) ? importedTokens : [];

      const next = [
        entry,
        ...existing.filter(
          (x) => `${x.network}:${String(x.address).toLowerCase()}` !== key,
        ),
      ].slice(0, 25);

      saveImportedTokens(next);

      setImportStatus("Imported ✅ Token Actions is ready.");
      refreshUserBalance();
    } catch (err) {
      console.error(err);
      setImportStatus(safeApiError(err, "Import failed."));
    }
  }, [
    importAddress,
    selectedNetwork,
    walletAddress,
    isAuthed,
    detectTokenTypeFromBackend,
    importedTokens,
    saveImportedTokens,
    refreshUserBalance,
  ]);

  useEffect(() => {
    setMintStatusText("");
  }, [mintContractAddress, mintTokenId, mintAmount, mintDataBytes, mintTo]);

  useEffect(() => {
    loadAccountUsage();
  }, [loadAccountUsage]);

  /* -------------------------------------------------------------------
   * Deploy Token (User Pays Gas)
   * ----------------------------------------------------------------- */

  const deploymentUsageSummary = useMemo(() => {
    const usage = accountUsage?.usage || {};
    const limits = accountUsage?.limits || {};
    const plan = normalizePlanCode(accountUsage?.plan || "free");
    const role = String(accountUsage?.role || "user").toLowerCase();
    const isPrivileged = Boolean(accountUsage?.isPrivileged);
    const isUnlimited = Boolean(limits?.unlimited);

    const deploymentsUsed = Number(usage?.deploymentsUsed || 0);
    const deploymentsLimit = Number(limits?.deployments || 0);

    return {
      plan,
      role,
      isPrivileged,
      isUnlimited,
      deploymentsUsed,
      deploymentsLimit,
      limitReached:
        !isPrivileged &&
        !isUnlimited &&
        deploymentsLimit > 0 &&
        deploymentsUsed >= deploymentsLimit,
    };
  }, [accountUsage]);

  const TOKEN_TYPE_ACCESS = useMemo(
    () => ({
      ERC20: "free",
      ERC721: "pro",
      ERC1155: "pro",
    }),
    [],
  );

  const MODULE_ACCESS = useMemo(
    () => ({
      mintable: "free",
      burnable: "free",
      pausable: "pro",
    }),
    [],
  );

  const MINT_PLAN_ACCESS = useMemo(
    () => ({
      ERC20: "pro",
      ERC721: "pro",
      ERC1155: "pro",
    }),
    [],
  );

  const currentPlan = deploymentUsageSummary.plan;
  const currentRole = deploymentUsageSummary.role;
  const isPrivilegedUser = deploymentUsageSummary.isPrivileged;
  const isUnlimitedUser = deploymentUsageSummary.isUnlimited;
  const hasPaidAccess = hasPaidFeatureAccess(currentPlan);

  const includedTokenTypesText = isPrivilegedUser
    ? "ERC20, ERC721, ERC1155 (owner/admin access)"
    : hasPaidAccess
      ? "ERC20, ERC721, ERC1155"
      : "ERC20 only";

  const deploymentUsageText = isUnlimitedUser
    ? `${deploymentUsageSummary.deploymentsUsed} / Unlimited`
    : `${deploymentUsageSummary.deploymentsUsed} / ${deploymentUsageSummary.deploymentsLimit || "—"}`;

  const forkSimulationUsageText = isUnlimitedUser
    ? `${Number(accountUsage?.usage?.forkSimulationsUsed || 0)} / Unlimited`
    : `${Number(accountUsage?.usage?.forkSimulationsUsed || 0)} / ${Number(
      accountUsage?.limits?.forkSimulations || 0,
    ) || "—"}`;

  const forkSimulationLimitReached =
    !isPrivilegedUser &&
    !isUnlimitedUser &&
    Number(accountUsage?.limits?.forkSimulations || 0) > 0 &&
    Number(accountUsage?.usage?.forkSimulationsUsed || 0) >=
    Number(accountUsage?.limits?.forkSimulations || 0);

  const isTokenTypeLocked = useCallback(
    (type) => {
      if (isPrivilegedUser) return false;
      const requiredPlan = TOKEN_TYPE_ACCESS[type] || "free";
      return requiredPlan === "pro" && !hasPaidAccess;
    },
    [TOKEN_TYPE_ACCESS, hasPaidAccess, isPrivilegedUser],
  );

  const isModuleLocked = useCallback(
    (moduleName) => {
      if (isPrivilegedUser) return false;
      const requiredPlan = MODULE_ACCESS[moduleName] || "free";
      return requiredPlan === "pro" && !hasPaidAccess;
    },
    [MODULE_ACCESS, hasPaidAccess, isPrivilegedUser],
  );

  const isMintLocked = useCallback(
    (tokenType) => {
      if (isPrivilegedUser) return false;
      const requiredPlan = MINT_PLAN_ACCESS[tokenType] || "free";
      return requiredPlan === "pro" && !hasPaidAccess;
    },
    [MINT_PLAN_ACCESS, hasPaidAccess, isPrivilegedUser],
  );

  const hasLockedModuleSelected = useMemo(() => {
    return selectedModules.some((moduleName) => isModuleLocked(moduleName));
  }, [selectedModules, isModuleLocked]);

  const handleCheckboxChange = useCallback(
    (e) => {
      const { name, checked } = e.target;

      if (isModuleLocked(name)) {
        setStatusText(
          "This token feature is available on Starter and Pro plans.",
        );
        return;
      }

      setModules((prev) => ({ ...prev, [name]: checked }));
    },
    [isModuleLocked],
  );

  const handleTokenTypeChange = useCallback(
    (e) => {
      const nextType = String(e.target.value || "")
        .trim()
        .toUpperCase();

      if (isTokenTypeLocked(nextType)) {
        setStatusText("ERC721 and ERC1155 are available on Starter and Pro plans.");
        return;
      }

      setTokenType(nextType);
    },
    [isTokenTypeLocked],
  );

  const handlePreflightSimulation = useCallback(async () => {
    if (!isAuthed) {
      setStatusText("Sign in to run Preflight Simulation.");
      return;
    }

    if (!["ERC20", "ERC721", "ERC1155"].includes(tokenType)) {
      setStatusText("Preflight Simulation currently supports ERC20, ERC721, and ERC1155.");
      return;
    }

    if (!deploySelectedNetworks.length) {
      setStatusText("Select one network for Preflight Simulation.");
      return;
    }

    if (deploySelectedNetworks.length > 1) {
      setStatusText("Preflight Simulation currently runs one network at a time. Select one network.");
      return;
    }

    if (!tokenName.trim()) {
      setStatusText("Token name is required before simulation.");
      return;
    }

    if (tokenType !== "ERC1155" && !tokenSymbol.trim()) {
      setStatusText("Token symbol is required before simulation.");
      return;
    }

    if (tokenType === "ERC1155" && !baseURI.trim()) {
      setStatusText("ERC1155 metadata base URI is required before simulation.");
      return;
    }

    if (tokenType === "ERC721" && !baseURI.trim()) {
      setStatusText(
        "ERC721 metadata base URI is required. Example: ipfs://YOUR_METADATA_CID/ resolves token ID 1 to ipfs://YOUR_METADATA_CID/1.json."
      );
      return;
    }

    let royaltyFeeToSend = "0";
    let royaltyReceiverToSend = "";

    if (tokenType === "ERC721" || tokenType === "ERC1155") {
      const rawRoyaltyFee = String(royaltyFeeNumerator ?? "0").trim() || "0";

      if (!/^[0-9]+$/.test(rawRoyaltyFee)) {
        setStatusText("Royalty fee must be a whole number in basis points. Example: 500 = 5%.");
        return;
      }

      const royaltyFeeNumber = Number(rawRoyaltyFee);

      if (!Number.isInteger(royaltyFeeNumber) || royaltyFeeNumber < 0 || royaltyFeeNumber > 10000) {
        setStatusText("Royalty fee must be between 0 and 10000 basis points. Example: 500 = 5%.");
        return;
      }

      royaltyFeeToSend = String(royaltyFeeNumber);
      royaltyReceiverToSend = String(royaltyReceiver || "").trim();

      if (royaltyFeeNumber > 0 && royaltyReceiverToSend && !isEvmAddress(royaltyReceiverToSend)) {
        setStatusText("Royalty receiver must be a valid 0x address.");
        return;
      }
    }

    const rawSupply = String(erc20InitialSupply ?? "").trim();

    if (rawSupply !== "" && !/^[0-9]+(\.[0-9]+)?$/.test(rawSupply)) {
      setStatusText("Initial supply must be a valid number.");
      return;
    }

    setPreflightLoading(true);
    setPreflightResult(null);
    setStatusText("Running Preflight Simulation on a fork of the selected network…");

    try {
      const networkKey = deploySelectedNetworks[0];

      const res = await api.post(
        "/simulations/fork/preflight",
        {
          tokenType,
          network: networkKey,
          tokenName: tokenName.trim(),
          tokenSymbol: tokenType === "ERC1155" ? "" : tokenSymbol.trim(),
          initialSupply: rawSupply || "0",
          baseURI:
            tokenType === "ERC721" || tokenType === "ERC1155"
              ? baseURI.trim()
              : undefined,
          royaltyReceiver:
            tokenType === "ERC721" || tokenType === "ERC1155"
              ? royaltyReceiverToSend
              : undefined,
          royaltyFeeNumerator:
            tokenType === "ERC721" || tokenType === "ERC1155"
              ? royaltyFeeToSend
              : undefined,
          mintable: Boolean(modules.mintable),
          burnable: Boolean(modules.burnable),
          pausable: Boolean(modules.pausable),
        },
        {
          timeout: 120000,
        },
      );

      setPreflightResult(res.data || null);

      if (res.data?.simulation?.ok) {
        setStatusText("Preflight Simulation passed ✅ You can deploy with more confidence.");
        pushActivity("preflight", `Preflight passed for ${tokenName} on ${networkKey}`);
      } else {
        setStatusText(
          res.data?.simulation?.error ||
          res.data?.error ||
          "Preflight Simulation failed.",
        );
        await loadAccountUsage();
      }

      await loadAccountUsage();
    } catch (err) {
      console.error("[preflight] failed:", err);

      const code = err?.response?.data?.code;
      const details = err?.response?.data?.details;

      if (code === "FORK_SIMULATION_LIMIT_REACHED") {
        setStatusText(
          `Preflight Simulation limit reached (${details?.used ?? "?"} / ${details?.limit ?? "?"
          }). Upgrade to continue.`,
        );
        await loadAccountUsage();
        return;
      }

      setStatusText(safeApiError(err, "Preflight Simulation failed."));
    } finally {
      setPreflightLoading(false);
    }
  }, [
    isAuthed,
    tokenType,
    tokenName,
    tokenSymbol,
    erc20InitialSupply,
    baseURI,
    royaltyFeeNumerator,
    royaltyReceiver,
    modules,
    deploySelectedNetworks,
    pushActivity,
    loadAccountUsage,
  ]);

  const handleDeploy = useCallback(async () => {
    let activeWalletAddress = "";

    if (!authReady || restoreLoading) {
      setStatusText("Checking your session. Please wait a moment.");
      return;
    }

    if (!isAuthed) {
      setStatusText("Please connect wallet and sign in from the top bar before deploying.");
      return;
    }

    if (!walletAddress || !isEvmAddress(walletAddress)) {
      setStatusText("Please connect wallet from the top bar before deploying.");
      return;
    }

    activeWalletAddress = String(walletAddress).trim();

    if (deploymentUsageSummary.limitReached) {
      setStatusText(
        `You have reached your deployment limit (${deploymentUsageSummary.deploymentsUsed} / ${deploymentUsageSummary.deploymentsLimit}). Upgrade your plan to deploy more tokens.`,
      );
      return;
    }

    if (isTokenTypeLocked(tokenType)) {
      setStatusText(
        "Your plan supports ERC20 only. Upgrade to Starter or Pro to deploy ERC721 and ERC1155.",
      );
      return;
    }

    if (hasLockedModuleSelected) {
      setStatusText(
        "Selected token features require Starter or Pro. Upgrade to continue.",
      );
      return;
    }

    if (!deploySelectedNetworks.length) {
      setStatusText("Select at least one deployment network.");
      return;
    }

    if (!tokenName.trim()) {
      setStatusText("Token name is required.");
      return;
    }

    if (tokenType !== "ERC1155" && !tokenSymbol.trim()) {
      setStatusText("Token symbol is required.");
      return;
    }

    if (tokenType === "ERC1155" && !baseURI.trim()) {
      setStatusText(
        "ERC1155 needs a metadata base URI. You can keep the default example and replace YOUR_METADATA_CID later with your real IPFS CID."
      );
      return;
    }

    if (tokenType === "ERC721" && !baseURI.trim()) {
      setStatusText(
        "ERC721 needs a metadata base URI. Example: ipfs://YOUR_METADATA_CID/ resolves token ID 1 to ipfs://YOUR_METADATA_CID/1.json."
      );
      return;
    }

    let royaltyFeeToSend = "0";
    let royaltyReceiverToSend = "";

    if (tokenType === "ERC721" || tokenType === "ERC1155") {
      const rawRoyaltyFee = String(royaltyFeeNumerator ?? "0").trim() || "0";

      if (!/^[0-9]+$/.test(rawRoyaltyFee)) {
        setStatusText("Royalty fee must be a whole number in basis points. Example: 500 = 5%.");
        return;
      }

      const royaltyFeeNumber = Number(rawRoyaltyFee);

      if (!Number.isInteger(royaltyFeeNumber) || royaltyFeeNumber < 0 || royaltyFeeNumber > 10000) {
        setStatusText("Royalty fee must be between 0 and 10000 basis points. Example: 500 = 5%.");
        return;
      }

      royaltyFeeToSend = String(royaltyFeeNumber);
      royaltyReceiverToSend = String(royaltyReceiver || "").trim();

      if (royaltyFeeNumber > 0 && royaltyReceiverToSend && !isEvmAddress(royaltyReceiverToSend)) {
        setStatusText("Royalty receiver must be a valid 0x address.");
        return;
      }
    }

    let decimalsToSend = 18;
    if (tokenType === "ERC20") {
      const raw = String(erc20Decimals ?? "").trim();
      const num = raw === "" ? 18 : Number(raw);
      if (!Number.isInteger(num) || num < 0 || num > 18) {
        setStatusText("Decimals must be an integer between 0 and 18.");
        return;
      }
      decimalsToSend = num;
    }

    let initialSupplyToSend = "";
    if (tokenType === "ERC20") {
      const rawSupply = String(erc20InitialSupply ?? "").trim();

      if (rawSupply !== "" && !/^[0-9]+(\.[0-9]+)?$/.test(rawSupply)) {
        setStatusText("Initial supply must be a valid number.");
        return;
      }

      initialSupplyToSend = rawSupply;
    }

    try {
      const wallet = await getActiveWalletContext();
      const connectedAddress = String(wallet?.address || "").trim();

      if (!isEvmAddress(connectedAddress)) {
        throw new Error("No active wallet address found.");
      }

      if (connectedAddress.toLowerCase() !== activeWalletAddress.toLowerCase()) {
        setStatusText("Connected wallet changed. Please use the same wallet shown in the top bar.");
        return;
      }
    } catch {
      setStatusText("Please connect wallet from the top bar before deploying.");
      return;
    }

    setDeployLoading(true);
    setStatusText("");
    setDeployResult(null);
    setMintResult(null);

    try {
      setStatusText("Preparing deployment transaction…");

      const results = [];

      for (const networkKey of deploySelectedNetworks) {
        const net = networks.find((n) => n.key === networkKey);
        if (!net?.chainId) {
          throw new Error(`Network config missing chainId for ${networkKey}.`);
        }

        setStatusText(`Preparing deployment for ${net.label}…`);

        const payload = {
          type: tokenType,
          tokenName,
          tokenSymbol: tokenType === "ERC1155" ? "" : tokenSymbol,
          modules: selectedModules,
          baseURI:
            tokenType === "ERC721" || tokenType === "ERC1155"
              ? baseURI.trim()
              : undefined,
          royaltyReceiver:
            tokenType === "ERC721" || tokenType === "ERC1155"
              ? royaltyReceiverToSend
              : undefined,
          royaltyFeeNumerator:
            tokenType === "ERC721" || tokenType === "ERC1155"
              ? royaltyFeeToSend
              : undefined,
          decimals: tokenType === "ERC20" ? decimalsToSend : undefined,
          initialSupply: tokenType === "ERC20" ? initialSupplyToSend : undefined,

          network: networkKey,
          deploymentMode: "user",
          userAddress: activeWalletAddress,
        };

        const prepRes = await api.post("/create-token", payload);
        const prep = prepRes.data;

        if (!prep?.tx?.data) {
          throw new Error(
            `Backend did not return an unsigned deployment tx for ${net.label}.`,
          );
        }
        if (!prep?.requestId) {
          throw new Error(`Backend did not return requestId for ${net.label}.`);
        }

        setStatusText(`Switching wallet to ${net.label}…`);
        await ensureWalletOnChain(net.chainId);

        setStatusText(`Confirm deployment for ${net.label} in your wallet…`);
        const txHash = await sendUserTransaction(
          prep.tx,
          activeWalletAddress,
          net.chainId,
        );

        savePendingDeploy({
          requestId: prep.requestId,
          network: networkKey,
          txHash,
          artifactKey: prep.artifactKey,
          constructorArgs: prep.constructorArgs || [],
          userAddress: activeWalletAddress,

          tokenType,
          token: {
            tokenName: tokenName.trim(),
            tokenSymbol: tokenType === "ERC1155" ? null : tokenSymbol.trim(),
            baseURI:
              tokenType === "ERC721" || tokenType === "ERC1155"
                ? baseURI.trim()
                : null,
            royaltyReceiver:
              tokenType === "ERC721" || tokenType === "ERC1155"
                ? royaltyReceiverToSend || activeWalletAddress
                : null,
            royaltyFeeNumerator:
              tokenType === "ERC721" || tokenType === "ERC1155"
                ? royaltyFeeToSend
                : null,
            modules: selectedModules,
            decimals: tokenType === "ERC20" ? decimalsToSend : null,
            initialSupply:
              tokenType === "ERC20" ? initialSupplyToSend || null : null,
          },
        });

        setStatusText(
          `Transaction sent on ${net.label} (${shorten(txHash)}). Confirming…`,
        );

        const confirmBody = {
          requestId: prep.requestId,
          network: networkKey,
          txHash,
          artifactKey: prep.artifactKey,
          constructorArgs: prep.constructorArgs || [],
          userAddress: activeWalletAddress,
          confirmations: net.confirmations || 2,

          tokenType,
          token: {
            tokenName: tokenName.trim(),
            tokenSymbol: tokenType === "ERC1155" ? null : tokenSymbol.trim(),
            baseURI:
              tokenType === "ERC721" || tokenType === "ERC1155"
                ? baseURI.trim()
                : null,
            royaltyReceiver:
              tokenType === "ERC721" || tokenType === "ERC1155"
                ? royaltyReceiverToSend || activeWalletAddress
                : null,
            royaltyFeeNumerator:
              tokenType === "ERC721" || tokenType === "ERC1155"
                ? royaltyFeeToSend
                : null,
            modules: selectedModules,
            decimals: tokenType === "ERC20" ? decimalsToSend : null,
            initialSupply: tokenType === "ERC20" ? initialSupplyToSend || null : null,
          },
        };

        const confirmRes = await api.post("/deployment/confirm", confirmBody, {
          timeout: 120000,
        });

        clearPendingDeploy();

        results.push(confirmRes.data);

        pushActivity(
          "token-deploy",
          `Deployed: ${tokenName} (${tokenType}) on ${networkKey}`,
        );
      }

      setDeployResult(results);
      setStatusText(
        `Token deployed on ${results.length} network${results.length === 1 ? "" : "s"} ✅`,
      );

      refreshRecentDeploymentsUntilSettled();

      (async () => {
        const delays = [10000, 25000, 45000];

        for (const delay of delays) {
          await new Promise((resolve) => setTimeout(resolve, delay));

          for (const result of results) {
            try {
              await api.post("/verification/sync-status", {
                network: result.network,
                contractAddress: result.contractAddress || result.address,
                txHash: result.txHash || null,
                requestId: result.requestId || null,
              });
            } catch (err) {
              console.warn("[auto-verification-sync] skipped:", safeApiError(err, "Verification sync failed."));
            }
          }

          await loadRecentDeployments();
        }
      })();

      await loadAccountUsage();

      if (results.length === 1) {
        const onlyResult = results[0];
        const deployedAddress = extractDeployedAddress(onlyResult);

        if (isNonEmptyString(deployedAddress)) {
          setSelectedNetwork(
            onlyResult.network || deploySelectedNetworks[0] || "sepolia",
          );
          setMintContractAddress(deployedAddress);
          setMintTokenType(onlyResult.tokenType || tokenType);
          setMintTo(activeWalletAddress);
          setAutoDetectEnabled(true);
          setMintStatusText(
            "Deployment completed. Token Actions has been prefilled for the deployed token.",
          );
        }
      } else {
        setMintContractAddress("");
        setMintStatusText(
          "Multi-network deployment completed. Choose one deployed token from the results below to continue with minting.",
        );
      }
    } catch (err) {
      console.error(err);

      const code = err?.response?.data?.code;
      const details = err?.response?.data?.details;

      if (code === "DEPLOYMENT_LIMIT_REACHED") {
        setStatusText(
          `You have reached your deployment limit (${details?.used ?? "?"} / ${details?.limit ?? "?"}). Upgrade your plan to deploy more tokens.`,
        );
        await loadAccountUsage();
      } else {
        setStatusText(safeApiError(err, "Deployment failed."));
      }
    } finally {
      setDeployLoading(false);
    }
  }, [
    authReady,
    restoreLoading,
    isAuthed,
    walletAddress,
    tokenName,
    tokenType,
    tokenSymbol,
    erc20Decimals,
    erc20InitialSupply,
    baseURI,
    royaltyFeeNumerator,
    royaltyReceiver,
    selectedModules,
    deploySelectedNetworks,
    networks,
    ensureWalletOnChain,
    sendUserTransaction,
    getActiveWalletContext,
    pushActivity,
    refreshRecentDeploymentsUntilSettled,
    loadRecentDeployments,
    deploymentUsageSummary,
    loadAccountUsage,
    isTokenTypeLocked,
    hasLockedModuleSelected,
  ]);

  /* -------------------------------------------------------------------
   * Mint Token (User Pays Gas)
   * ----------------------------------------------------------------- */

  const handleMint = useCallback(async () => {
    setStatusText("");
    setMintStatusText("");

    if (isMintLocked(mintUiTokenType)) {
      setMintStatusText("Minting is available on Starter and Pro plans.");
      return;
    }

    if (!walletAddress) {
      setMintStatusText("Connect wallet in the header to mint.");
      return;
    }

    if (!isEvmAddress(mintContractAddress)) {
      setMintStatusText("Enter a valid contract address (0x...).");
      return;
    }

    if (!isEvmAddress(mintTo)) {
      setMintStatusText("Enter a valid 'Mint To' address (0x...).");
      return;
    }

    if (mintNeedsTokenId && !isIntegerString(mintTokenId)) {
      setMintStatusText(
        mintUiTokenType === "ERC1155"
          ? "ERC1155 Token ID must be an integer (for example: 0, 1, or 42)."
          : "Token ID must be an integer (e.g. 0, 1, 42)."
      );
      return;
    }

    if (mintNeedsAmount) {
      if (!isNonEmptyString(mintAmount)) {
        setMintStatusText("Amount is required.");
        return;
      }
      if (mintUiTokenType === "ERC1155" && !isIntegerString(mintAmount)) {
        setMintStatusText(
          "ERC1155 Amount must be an integer quantity (for example: 1, 5, or 10). Decimals are not supported."
        );
        return;
      }
    }

    if (mintNeedsBytes && !isHexBytes(mintDataBytes)) {
      setMintStatusText(
        "Advanced data must be hex and start with 0x. Leave it as 0x unless you specifically need custom contract data."
      );
      return;
    }

    setMintLoading(true);
    setMintResult(null);
    setMintStatusText("");

    try {
      const net = selectedNetworkObj;
      if (!net?.chainId) throw new Error("Selected network missing chainId.");

      const isCustomMint = isCustomTokenInfo(tokenInfo);
      const actionCapabilities = getCustomActionCapabilities(tokenInfo);

      if (isCustomMint) {
        if (
          actionCapabilities.mint === false &&
          actionCapabilities.mintable === false
        ) {
          throw new Error("This custom contract does not expose a supported mint function.");
        }

        const customMintArgs = buildCustomMintArgs({
          tokenType: mintUiTokenType,
          to: mintTo.trim(),
          tokenId: mintTokenId.trim(),
          amount: mintAmount.trim(),
          data: mintDataBytes.trim(),
          tokenInfo,
        });

        setMintStatusText("Preparing custom mint transaction…");

        const prep = await prepareCustomTokenAction({
          tokenInfo,
          network: selectedNetwork,
          contractAddress: mintContractAddress.trim(),
          action: "mint",
          args: customMintArgs,
        });

        setMintStatusText(`Switching wallet to ${net.label}…`);
        await ensureWalletOnChain(net.chainId);

        setMintStatusText("Confirm the custom mint in your wallet…");
        const txHash = await sendUserTransaction(
          prep.tx,
          walletAddress,
          net.chainId,
        );

        setMintStatusText(`Mint tx sent (${shorten(txHash)}). Confirming…`);

        const confirmData = await confirmWithRetry(
          () =>
            confirmCustomTokenAction({
              actionRequestId: prep.actionRequestId,
              txHash,
              confirmations: net.confirmations || 2,
            }),
          { attempts: 6, delayMs: 2000 },
        );

        setMintResult(confirmData);
        setMintStatusText("Custom mint confirmed ✅");

        pushActivity(
          "mint",
          `Minted custom ${mintUiTokenType} on ${selectedNetwork}`,
        );

        refreshUserBalance();
        return;
      }

      const artifactKey =
        tokenInfo?.deployment?.artifactKey ||
        tokenInfo?.deployment?.artifact_key ||
        artifactKeyForToken(mintUiTokenType, selectedModules);

      if (!artifactKey) {
        throw new Error(
          "Missing artifactKey for this token. Try refreshing token info or redeploy via Krionex.",
        );
      }

      setMintStatusText("Preparing mint transaction…");

      const prepRes = await api.post("/mint", {
        network: selectedNetwork,
        mode: "user",
        tokenType: mintUiTokenType,
        artifactKey,
        contractAddress: mintContractAddress.trim(),
        to: mintTo.trim(),
        userAddress: walletAddress,

        tokenId: mintUiTokenType === "ERC721" ? mintTokenId.trim() : undefined,
        amount: mintUiTokenType !== "ERC721" ? mintAmount.trim() : undefined,
        amountFormat: mintUiTokenType === "ERC20" ? "human" : undefined,

        id: mintUiTokenType === "ERC1155" ? mintTokenId.trim() : undefined,
        data: mintUiTokenType === "ERC1155" ? mintDataBytes.trim() : undefined,
      });

      const prep = prepRes.data;

      if (!prep?.ok || !prep?.tx?.data) {
        throw new Error(prep?.error || "Backend did not return mint tx.");
      }

      setMintStatusText(`Switching wallet to ${net.label}…`);
      await ensureWalletOnChain(net.chainId);

      setMintStatusText("Confirm the mint in your wallet…");
      const txHash = await sendUserTransaction(
        prep.tx,
        walletAddress,
        net.chainId,
      );

      setMintStatusText(`Mint tx sent (${shorten(txHash)}). Confirming…`);

      const confirmRes = await confirmWithRetry(
        () =>
          api.post("/mint/confirm", {
            network: selectedNetwork,
            txHash,
            contractAddress: mintContractAddress.trim(),
            userAddress: walletAddress,
            tokenType: mintUiTokenType,
            artifactKey,
            to: mintTo.trim(),
            tokenId: mintTokenId ? mintTokenId.trim() : null,
            amount: mintAmount ? mintAmount.trim() : null,
            amountFormat: mintUiTokenType === "ERC20" ? "human" : undefined,
            decimals:
              mintUiTokenType === "ERC20"
                ? (tokenInfo?.decimals ??
                  tokenInfo?.deployment?.token?.decimals ??
                  18)
                : undefined,
            data:
              mintUiTokenType === "ERC1155" ? mintDataBytes.trim() : undefined,
            confirmations: net.confirmations || 2,
          }),
        { attempts: 6, delayMs: 2000 },
      );

      setMintResult(confirmRes.data);
      setMintStatusText("Mint confirmed ✅");

      pushActivity("mint", `Minted (${mintUiTokenType}) on ${selectedNetwork}`);

      if (mintUiTokenType === "ERC1155") {
        const mintedId = (
          confirmRes?.data?.tokenId ??
          confirmRes?.data?.id ??
          ""
        )
          .toString()
          .trim();

        if (isIntegerString(mintedId) && !isIntegerString(mintTokenId)) {
          setMintTokenId(mintedId);
        }
      }

      refreshUserBalance();
    } catch (err) {
      const code = err?.code;
      const nestedCode = err?.info?.error?.code;
      const message = String(
        err?.shortMessage ||
        err?.reason ||
        err?.message ||
        err?.info?.error?.message ||
        "",
      ).toLowerCase();

      const isRejected =
        code === "ACTION_REJECTED" ||
        code === 4001 ||
        nestedCode === 4001 ||
        nestedCode === 5000 ||
        message.includes("user denied") ||
        message.includes("user rejected") ||
        message.includes("rejected") ||
        message.includes("denied transaction signature");

      if (!isRejected) {
        console.error("[mint] failed:", err);
      }

      setMintStatusText(humanizeWalletActionError(err, "Mint failed."));
    } finally {
      setMintLoading(false);
    }
  }, [
    walletAddress,
    mintContractAddress,
    mintTo,
    mintTokenId,
    mintAmount,
    mintDataBytes,
    mintNeedsTokenId,
    mintNeedsAmount,
    mintNeedsBytes,
    mintUiTokenType,
    tokenInfo,
    selectedNetwork,
    selectedNetworkObj,
    selectedModules,
    ensureWalletOnChain,
    sendUserTransaction,
    artifactKeyForToken,
    pushActivity,
    refreshUserBalance,
    confirmWithRetry,
    isMintLocked,
  ]);

  /* -------------------------------------------------------------------
   * Derived UI rows
   * ----------------------------------------------------------------- */

  const recentRows = useMemo(() => {
    return (recentDeployments || []).map((d) => {
      const tokenName = getDeploymentDisplayName(d);
      const tokenType = d?.tokenType || d?.type || "-";
      const network = d?.network || "-";
      const address = getDeploymentAddress(d);
      const txHash = getDeploymentTxHash(d);
      const explorerUrl = d?.explorerUrl || d?.etherscanUrl || "";
      const explorerTxUrl = d?.explorerTxUrl || "";
      const verificationStatus = String(
        d?.verificationStatus || "not_requested",
      );

      const decimals = d?.token?.decimals ?? d?.decimals ?? null;
      const initialSupplyHuman =
        d?.token?.initialSupplyHuman ?? d?.initialSupplyHuman ?? null;
      const initialSupply =
        d?.token?.initialSupply ?? d?.initialSupply ?? null;

      return {
        raw: d,

        sourceMode: d?.sourceMode || "standard",
        isCustom: isCustomDeploymentRecord(d),

        tokenName: String(tokenName || "-"),
        tokenType: String(tokenType || "-"),
        network: String(network || "-"),
        address: String(address || ""),
        txHash: String(txHash || ""),
        explorerUrl: String(explorerUrl || ""),
        explorerTxUrl: String(explorerTxUrl || ""),
        verificationStatus,

        decimals,
        initialSupplyHuman,
        initialSupply,

        requestId: d?.requestId ? String(d.requestId) : "",
        deploymentFile: d?.deploymentFile ? String(d.deploymentFile) : "",
        customVersionId: d?.customVersionId ? String(d.customVersionId) : "",
        contractName: d?.contractName ? String(d.contractName) : "",
        artifactKey: d?.artifactKey ? String(d.artifactKey) : "",

        download: getDeploymentDownload(d),
        bundleLabel: getDeploymentBundleLabel(d),
        hasVerificationContext: hasDeploymentVerificationContext(d),
      };
    });
  }, [recentDeployments]);

  const tokenDisplayName = useMemo(() => {
    if (!tokenInfo) return "";

    const onchainName = String(tokenInfo?.name || "").trim();
    const onchainSymbol = String(tokenInfo?.symbol || "").trim();

    const depName = String(
      tokenInfo?.deployment?.token?.tokenName ||
      tokenInfo?.tokenName ||
      ""
    ).trim();

    const depSymbol = String(
      tokenInfo?.deployment?.token?.tokenSymbol ||
      tokenInfo?.tokenSymbol ||
      ""
    ).trim();

    const name = onchainName || depName;
    const symbol = onchainSymbol || depSymbol;

    if (name && symbol) return `${name} (${symbol})`;
    if (name) return name;
    if (symbol) return symbol;

    const addr =
      String(tokenInfo?.address || "").trim() ||
      String(mintContractAddress || "").trim();

    if (mintUiTokenType === "ERC1155" && addr) {
      return `ERC1155 contract ${shorten(addr)}`;
    }

    if (mintUiTokenType === "ERC721" && addr) {
      return `ERC721 contract ${shorten(addr)}`;
    }

    if (mintUiTokenType === "ERC20" && addr) {
      return `ERC20 contract ${shorten(addr)}`;
    }

    return addr ? shorten(addr) : "";
  }, [tokenInfo, mintUiTokenType, mintContractAddress]);

  /* -------------------------------------------------------------------
   * UI
   * ----------------------------------------------------------------- */

  if (!authReady) {
    return (
      <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 text-center text-sm font-medium text-slate-700">
          Checking workspace session…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-7 text-[#050713]">
      <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.07)] space-y-7 md:p-8">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_12%_0%,rgba(197,155,69,0.13),transparent_35%),radial-gradient(circle_at_88%_0%,rgba(15,23,42,0.08),transparent_34%)]" />
        {/* Studio header (NO wallet/auth controls here) */}
        <div className="relative overflow-hidden rounded-[1.75rem] border border-slate-900 bg-[#050713] p-6 text-white shadow-[0_24px_70px_rgba(15,23,42,0.18)] md:p-7">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(230,201,120,0.20),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.07),transparent_32%)]" />

          <div className="relative flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-[#E6C978]/25 bg-[#E6C978]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#E6C978]">
                Studio workspace
              </div>

              <h2 className="mt-4 max-w-4xl text-3xl font-semibold tracking-[-0.045em] text-white sm:text-4xl">
                Deploy and operate contracts from one secure workspace.
              </h2>

              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">
                Configure contracts, select networks, confirm wallet actions, and keep deployment activity organized across supported EVM chains.
              </p>
            </div>

            <div className="hidden rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 text-right md:block">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Active flow
              </div>
              <div className="mt-1 text-sm font-semibold text-white">
                Configure → Confirm → Deploy
              </div>
            </div>
          </div>
        </div>

        {walletError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {walletError}
          </div>
        )}

        {/* Studio Network Settings */}
        <div className="rounded-[1.5rem] border border-slate-200 bg-[#F8FAFC] p-5 shadow-[0_12px_35px_rgba(15,23,42,0.04)] space-y-4 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold tracking-[-0.02em] text-[#050713]">
                Deployment settings
              </h2>
              <p className="text-xs text-slate-600">
                Multi-network deployment is configured below.
              </p>
            </div>

            <label className="flex select-none items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
              <input
                type="checkbox"
                checked={includeTestnets}
                onChange={(e) => setIncludeTestnets(e.target.checked)}
              />
              Show testnets
            </label>
          </div>

          {networksLoading && (
            <p className="text-sm text-slate-600">Loading networks…</p>
          )}
          {networksError && (
            <p className="text-sm text-red-600">{networksError}</p>
          )}
        </div>

        {/* Deploy Token */}
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)] space-y-5 md:p-6">
          <div>
            <div className="inline-flex rounded-full border border-[#E8D7AD] bg-[#FFF9EA] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9A7226]">
              Contract launch
            </div>

            <h2 className="mt-3 text-2xl font-semibold tracking-[-0.035em] text-[#050713]">
              Deploy your contract
            </h2>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Configure the contract, select one or more networks, then confirm deployment from your connected wallet.
            </p>
          </div>

          {/* MULTI NETWORK SELECTOR */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-[#050713]">
                  Deployment Networks
                </h3>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Choose one or more chains for this deployment.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-[#E8D7AD] hover:bg-[#FFF9EA] hover:text-[#8C6722]"
                  onClick={() => setDeploySelectedNetworks(networks.map((n) => n.key))}
                >
                  Select All
                </button>

                <button
                  type="button"
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  onClick={() => setDeploySelectedNetworks([])}
                >
                  Clear
                </button>
              </div>
            </div>

            {networksLoading ? (
              <p className="text-sm text-slate-600">Loading deployment networks…</p>
            ) : networksError ? (
              <p className="text-sm text-red-600">{networksError}</p>
            ) : (
              <div className="rounded-[1.5rem] border border-slate-200 bg-[#F8FAFC] p-4 shadow-inner shadow-slate-200/40">
                <button
                  type="button"
                  onClick={() => setNetworkPickerOpen((v) => !v)}
                  className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition hover:border-[#E8D7AD] hover:shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E8D7AD] bg-[#FFF9EA] text-lg">
                      🌐
                    </div>

                    <div>
                      <div className="text-sm font-semibold text-[#050713]">
                        Networks
                      </div>
                      <div className="text-xs text-slate-500">
                        {deploySelectedNetworkObjs.length > 0
                          ? `${deploySelectedNetworkObjs.length} selected`
                          : "No networks selected"}
                      </div>
                    </div>
                  </div>

                  <div
                    className={`text-lg text-slate-400 transition-transform ${networkPickerOpen ? "rotate-180" : ""
                      }`}
                  >
                    ⌄
                  </div>
                </button>

                {deploySelectedNetworkObjs.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {deploySelectedNetworkObjs.map((n) => {
                      const meta = getNetworkMeta(n);

                      return (
                        <button
                          key={n.key}
                          type="button"
                          onClick={() => toggleDeployNetwork(n.key)}
                          className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50"
                        >
                          {meta.logo && (
                            <img
                              src={meta.logo}
                              alt=""
                              className="h-4 w-4 rounded-full object-contain"
                            />
                          )}
                          {n.label}
                          <span className="text-indigo-400">×</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {networkPickerOpen && (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-4 grid grid-cols-2 gap-2 rounded-2xl bg-slate-100 p-1">
                      <button
                        type="button"
                        onClick={() => setNetworkPickerTab("mainnets")}
                        className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${networkPickerTab === "mainnets"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                          }`}
                      >
                        Mainnets
                      </button>

                      <button
                        type="button"
                        onClick={() => setNetworkPickerTab("testnets")}
                        className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${networkPickerTab === "testnets"
                          ? "bg-white text-amber-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                          }`}
                      >
                        Testnets
                      </button>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {(networkPickerTab === "mainnets"
                        ? mainnetNetworks
                        : testnetNetworks
                      ).map((n) => {
                        const selected = deploySelectedNetworks.includes(n.key);
                        const meta = getNetworkMeta(n);
                        const testnet = isTestnetNetwork(n);

                        return (
                          <button
                            key={n.key}
                            type="button"
                            onClick={() => toggleDeployNetwork(n.key)}
                            aria-pressed={selected}
                            className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-all duration-200 active:scale-[0.98] ${selected
                              ? "border-indigo-500 bg-gradient-to-br from-indigo-50 via-white to-blue-50 shadow-xl shadow-indigo-200/70 ring-2 ring-indigo-300"
                              : "border-slate-200 bg-white shadow-sm hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md"
                              }`}
                          >
                            {selected && (
                              <>
                                <div className="absolute inset-0 bg-indigo-500/5" />
                                <div className="absolute left-0 top-0 h-full w-1 bg-indigo-500" />
                                <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-indigo-500/20 blur-3xl" />
                              </>
                            )}

                            <div className="relative flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-3">
                                <div
                                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-gradient-to-br ${meta.tone} shadow-sm transition group-hover:scale-105`}
                                >
                                  {meta.logo ? (
                                    <img
                                      src={meta.logo}
                                      alt={`${n.label || n.key} logo`}
                                      className="h-7 w-7 rounded-full object-contain"
                                    />
                                  ) : (
                                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/80 text-[10px] font-bold text-slate-700">
                                      {getNetworkShortName(n)}
                                    </div>
                                  )}
                                </div>

                                <div className="min-w-0">
                                  <div className="truncate text-sm font-semibold text-slate-900">
                                    {n.label}
                                  </div>
                                  <div className="mt-1 text-xs text-slate-500">
                                    {n.chainId ? `Chain ID ${n.chainId}` : n.key}
                                  </div>
                                  <span
                                    className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${testnet
                                      ? "border border-amber-200 bg-amber-50 text-amber-700"
                                      : "border border-emerald-200 bg-emerald-50 text-emerald-700"
                                      }`}
                                  >
                                    {testnet ? "Testnet" : "Mainnet"}
                                  </span>
                                </div>
                              </div>

                              <div
                                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold transition ${selected
                                  ? "border-indigo-600 bg-indigo-600 text-white"
                                  : "border-slate-300 bg-white text-transparent group-hover:border-indigo-300"
                                  }`}
                              >
                                ✓
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-1 text-xs text-slate-500">
              {deploySelectedNetworkObjs.length > 0
                ? `${deploySelectedNetworkObjs.length} network${deploySelectedNetworkObjs.length === 1 ? "" : "s"
                } selected`
                : "No networks selected"}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Token Standard
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Choose the contract type you want to deploy. NFT and multi-token standards are gated by plan.
            </p>
          </div>

          <select
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
            value={tokenType}
            onChange={handleTokenTypeChange}
          >
            <option value="ERC20">ERC20</option>

            <option
              value="ERC721"
              disabled={!isPrivilegedUser && !hasPaidAccess}
            >
              ERC721{" "}
              {!isPrivilegedUser && !hasPaidAccess ? "🔒 Starter or Pro" : ""}
            </option>

            <option
              value="ERC1155"
              disabled={!isPrivilegedUser && !hasPaidAccess}
            >
              ERC1155{" "}
              {!isPrivilegedUser && !hasPaidAccess ? "🔒 Starter or Pro" : ""}
            </option>
          </select>
          <div className="pt-2">
            <div className="text-sm font-semibold text-slate-900">Token Metadata</div>
            <p className="mt-1 text-xs text-slate-500">
              Configure the token name, symbol, metadata URI, and standard-specific fields.
            </p>
          </div>

          <input
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
            placeholder="Token Name (e.g. MyToken)"
            value={tokenName}
            onChange={(e) => setTokenName(e.target.value)}
          />

          {tokenType !== "ERC1155" && (
            <input
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
              placeholder="Token Symbol (e.g. MTK)"
              value={tokenSymbol}
              onChange={(e) => setTokenSymbol(e.target.value)}
            />
          )}

          {tokenType === "ERC20" && (
            <div className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-600">
                    Decimals <span className="text-gray-400">(advanced)</span>
                  </label>
                  <span className="text-xs text-gray-400">0–18</span>
                </div>

                <input
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                  type="number"
                  min="0"
                  max="18"
                  step="1"
                  value={erc20Decimals}
                  onChange={(e) => setErc20Decimals(e.target.value)}
                  placeholder="18"
                />

                <p className="text-xs text-slate-500">
                  Default is 18. Use lower values (like 6) only if you really need it.
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-600">Initial Supply</label>
                  <span className="text-xs text-gray-400">Optional</span>
                </div>

                <input
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                  type="text"
                  inputMode="decimal"
                  value={erc20InitialSupply}
                  onChange={(e) => setErc20InitialSupply(e.target.value)}
                  placeholder="e.g. 1000"
                />

                <p className="text-xs text-slate-500">
                  Leave empty to deploy with zero supply. If you enter a value, that amount will be minted to your connected wallet during deployment.
                </p>
              </div>
            </div>
          )}

          {(tokenType === "ERC721" || tokenType === "ERC1155") && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  {tokenType === "ERC1155" ? "Base URI (metadata template)" : "Metadata Base URI"}
                </label>
                <span className="text-[11px] text-slate-400">
                  Required
                </span>
              </div>

              <input
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                placeholder={
                  tokenType === "ERC1155"
                    ? DEFAULT_ERC1155_BASE_URI
                    : DEFAULT_ERC721_BASE_URI
                }
                value={baseURI}
                onChange={(e) => setBaseURI(e.target.value)}
              />

              <p className="text-xs leading-5 text-slate-500">
                {tokenType === "ERC1155"
                  ? "This tells wallets where each token type’s metadata lives. The default example uses the standard {id} pattern. Replace YOUR_METADATA_CID with your real IPFS CID when ready."
                  : "This is the base path for ERC721 metadata. Token ID 1 resolves to ipfs://YOUR_METADATA_CID/1.json. Upload metadata files as 1.json, 2.json, etc."}
              </p>
              {(tokenType === "ERC721" || tokenType === "ERC1155") ? (
                <div className="rounded-2xl border border-[#E7D8AC] bg-[#FFF9EA] px-4 py-3 text-xs leading-5 text-[#7A5A17]">
                  Krionex deploys and manages the contract, but it does not automatically create NFT images or metadata JSON in this version.
                  Upload your metadata to IPFS, Arweave, or stable HTTPS storage first, then replace YOUR_METADATA_CID before production deployment.
                  {" "}
                  <Link to="/guide" className="font-semibold underline">
                    Learn metadata setup.
                  </Link>
                </div>
              ) : null}
            </div>
          )}

          {/* Royalties block goes here */}
          {(tokenType === "ERC721" || tokenType === "ERC1155") && (
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 space-y-3">
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Royalties
                  </label>
                  <span className="text-[11px] text-slate-400">Optional</span>
                </div>

                <p className="mt-1 text-xs leading-5 text-slate-500">
                  ERC2981 royalty info is stored on-chain. Marketplaces can read it, but payment enforcement depends on each marketplace.
                </p>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">
                    Royalty fee
                  </label>

                  <select
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                    value={royaltyFeeNumerator}
                    onChange={(e) => setRoyaltyFeeNumerator(e.target.value)}
                  >
                    <option value="0">0% — no royalty</option>
                    <option value="250">2.5%</option>
                    <option value="500">5%</option>
                    <option value="750">7.5%</option>
                    <option value="1000">10%</option>
                  </select>

                  <p className="text-xs text-slate-500">
                    Stored as basis points. Example: 500 = 5%.
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-600">
                    Royalty receiver
                  </label>

                  <input
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                    value={royaltyReceiver}
                    onChange={(e) => setRoyaltyReceiver(e.target.value)}
                    placeholder={walletAddress || "Defaults to connected wallet if royalty is enabled"}
                  />

                  <p className="text-xs text-slate-500">
                    Leave blank to use the connected wallet as the royalty receiver.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Token features block */}
          <div className="rounded-[1.5rem] border border-slate-200 bg-[#F8FAFC] p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)] space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold tracking-[-0.02em] text-[#050713]">
                Token features
              </h3>
              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold capitalize text-slate-600">
                {isPrivilegedUser
                  ? `Access: ${currentRole}`
                  : `Plan: ${currentPlan}`}
              </span>
            </div>

            <div className="space-y-3">
              <label className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#E8D7AD] hover:shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
                <div>
                  <div className="text-sm font-medium">Mintable</div>
                  <p className="text-xs ">Allow creating more tokens later.</p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    Free
                  </span>
                  <input
                    type="checkbox"
                    name="mintable"
                    checked={modules.mintable}
                    onChange={handleCheckboxChange}
                  />
                </div>
              </label>

              <label className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#E8D7AD] hover:shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
                <div>
                  <div className="text-sm font-medium">Burnable</div>
                  <p className="text-xs text-slate-600">
                    Allow tokens to be permanently destroyed.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    Free
                  </span>
                  <input
                    type="checkbox"
                    name="burnable"
                    checked={modules.burnable}
                    onChange={handleCheckboxChange}
                  />
                </div>
              </label>

              <label
                className={`flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-gradient-to-r from-white to-slate-50 p-4 shadow-sm transition hover:shadow-md ${isModuleLocked("pausable") ? "opacity-80" : ""
                  }`}
              >
                <div>
                  <div className="text-sm font-medium">Pausable</div>
                  <p className="text-xs text-slate-600">
                    Pause transfers for emergency control.
                  </p>
                  {isModuleLocked("pausable") && (
                    <div className="mt-1 text-[11px] font-medium text-indigo-600">
                      Upgrade required
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <span className="inline-flex rounded-full border border-[#E8D7AD] bg-[#FFF9EA] px-2.5 py-1 text-[11px] font-semibold text-[#8C6722]">
                    Starter or Pro
                  </span>
                  <input
                    type="checkbox"
                    name="pausable"
                    checked={modules.pausable}
                    onChange={handleCheckboxChange}
                    disabled={isModuleLocked("pausable")}
                  />
                </div>
              </label>
            </div>

            {!isPrivilegedUser && !hasPaidAccess && (
              <UpgradePrompt
                title="Unlock paid contract workflows"
                message="Deploy ERC721, ERC1155, and advanced token features with Starter or Pro access."
                ctaLabel="View Starter and Pro"
              />
            )}

            {isPrivilegedUser && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">
                {currentRole === "owner" ? "Owner" : "Admin"} access enabled.
                All token types and advanced token features are unlocked for
                testing.
              </div>
            )}
          </div>

          <CustomizeSolidityButton
            tokenType={tokenType}
            tokenName={tokenName}
            tokenSymbol={tokenSymbol}
            erc20Decimals={erc20Decimals}
            erc20InitialSupply={erc20InitialSupply}
            baseURI={baseURI}
            royaltyFeeNumerator={royaltyFeeNumerator}
            royaltyReceiver={royaltyReceiver}
            modules={modules}
            selectedNetwork={selectedNetwork}
            deploySelectedNetworks={deploySelectedNetworks}
            disabled={
              isTokenTypeLocked(tokenType) ||
              hasLockedModuleSelected ||
              deploymentUsageSummary.limitReached
            }
            disabledReason={
              isTokenTypeLocked(tokenType)
                ? "Your plan does not include this token type."
                : hasLockedModuleSelected
                  ? "One or more selected modules require a higher plan."
                  : deploymentUsageSummary.limitReached
                    ? "You have reached your deployment limit."
                    : ""
            }
          />

          {isAuthed && accountUsageLoading && (
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] px-4 py-3 text-sm font-medium text-slate-600 shadow-sm">
              Checking deployment limits…
            </div>
          )}

          {isAuthed && accountUsageError && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 shadow-sm">
              {accountUsageError}
            </div>
          )}

          {isAuthed && !accountUsageLoading && !accountUsageError && (
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] px-4 py-4 text-sm text-slate-700 shadow-sm space-y-2">
              <div>
                <span className="font-semibold">
                  {isPrivilegedUser ? "Current access:" : "Current plan:"}
                </span>{" "}
                <span className="capitalize">
                  {isPrivilegedUser ? currentRole : deploymentUsageSummary.plan}
                </span>
              </div>
              <div>
                <span className="font-semibold">Deployment usage:</span>{" "}
                {deploymentUsageText}
              </div>
              <div>
                <span className="font-semibold">Preflight simulations:</span>{" "}
                {forkSimulationUsageText}
              </div>
              <div>
                <span className="font-semibold">Included token types:</span>{" "}
                {includedTokenTypesText}
              </div>
            </div>
          )}

          {isAuthed &&
            !isPrivilegedUser &&
            deploymentUsageSummary.limitReached && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm space-y-1">
                <div className="font-semibold">
                  You have reached your deployment limit.
                </div>
                <div>
                  You are using {deploymentUsageSummary.deploymentsUsed} of{" "}
                  {deploymentUsageSummary.deploymentsLimit} deployments on the{" "}
                  {deploymentUsageSummary.plan} plan.
                </div>
                <div>
                  Upgrade your plan to deploy more tokens.{" "}
                  <Link to="/upgrade" className="font-semibold underline underline-offset-4">
                    View plans
                  </Link>
                </div>
              </div>
            )}

          <div className="rounded-[1.5rem] border border-[#E8D7AD] bg-[#FFF9EA] p-5 text-sm text-[#5F4616] shadow-[0_14px_40px_rgba(197,155,69,0.10)] space-y-4">
            <div>
              <div className="text-base font-semibold tracking-[-0.02em] text-[#050713]">
                Preflight simulation
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-700">
                Test your {tokenType} deployment on a fork before spending real gas.
              </p>
            </div>

            <button
              type="button"
              onClick={handlePreflightSimulation}
              disabled={
                preflightLoading ||
                deployLoading ||
                forkSimulationLimitReached ||
                restoreLoading ||
                !isAuthed
              }
              className="inline-flex w-full items-center justify-center rounded-xl border border-[#D9BE76] bg-[#050713] px-4 py-3 text-sm font-semibold text-white shadow-[0_14px_32px_rgba(15,23,42,0.18)] transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {!isAuthed
                ? "Connect Wallet in Header to Simulate"
                : preflightLoading
                  ? "Running simulation…"
                  : forkSimulationLimitReached
                    ? "Preflight Limit Reached"
                    : "Run Preflight Simulation"}
            </button>

            {preflightResult?.simulation && (
              <div className="rounded-2xl border border-[#E8D7AD] bg-white p-4 text-xs text-slate-700 shadow-sm space-y-2">
                <div className="font-semibold text-slate-900">
                  {preflightResult.simulation.ok
                    ? "Simulation passed ✅"
                    : "Simulation failed"}
                </div>

                {preflightResult.simulation.contractAddress && (
                  <div>
                    <span className="font-semibold">Fork contract:</span>{" "}
                    {shorten(preflightResult.simulation.contractAddress)}
                  </div>
                )}

                {preflightResult.simulation.networkLabel && (
                  <div>
                    <span className="font-semibold">Forked network:</span>{" "}
                    {preflightResult.simulation.networkLabel}
                  </div>
                )}

                {preflightResult.simulation.gas?.used && (
                  <div>
                    <span className="font-semibold">Gas used:</span>{" "}
                    {Number(preflightResult.simulation.gas.used).toLocaleString()}
                  </div>
                )}

                {preflightResult.simulation.gas?.estimatedCostNativeLow ? (
                  <div>
                    <span className="font-semibold">Approximate wallet fee range:</span>{" "}
                    ~{Number(preflightResult.simulation.gas.estimatedCostNativeLow).toPrecision(3)}
                    {" – "}
                    {Number(preflightResult.simulation.gas.estimatedCostNativeAggressive).toPrecision(3)}{" "}
                    {preflightResult.simulation.gas.nativeSymbol || "native"}
                  </div>
                ) : preflightResult.simulation.gas?.estimatedCostNative ? (
                  <div>
                    <span className="font-semibold">Approximate wallet fee from:</span>{" "}
                    ~{Number(preflightResult.simulation.gas.estimatedCostNative).toPrecision(3)}{" "}
                    {preflightResult.simulation.gas.nativeSymbol || "native"}
                  </div>
                ) : null}

                {preflightResult.simulation.gas?.estimatedCostUsdLow ? (
                  <div>
                    <span className="font-semibold">Approximate fee value range:</span>{" "}
                    ~${Number(preflightResult.simulation.gas.estimatedCostUsdLow).toFixed(2)}
                    {" – "}
                    ${Number(preflightResult.simulation.gas.estimatedCostUsdAggressive).toFixed(2)}
                    <span className="text-xs text-gray-500 ml-1">(low to aggressive)</span>
                  </div>
                ) : preflightResult.simulation.gas?.estimatedCostUsd ? (
                  <div>
                    <span className="font-semibold">Approximate fee value from:</span>{" "}
                    ~${Number(preflightResult.simulation.gas.estimatedCostUsd).toFixed(2)}
                    <span className="text-xs text-gray-500 ml-1">(estimate)</span>
                  </div>
                ) : preflightResult.simulation.gas?.isTestnet ? (
                  <div className="text-xs text-slate-500">
                    USD value is not shown for testnets because testnet gas uses faucet funds.
                  </div>
                ) : null}

                <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-800">
                  This range is a preflight estimate from a fork simulation. Your wallet may
                  show Low, Market, or Aggressive fee options before confirmation. The final
                  fee can be lower or higher depending on live network fees, wallet fee
                  settings, and L2/L1 fee components.
                </div>

                {preflightResult.simulation.token?.totalSupplyHuman != null && (
                  <div>
                    <span className="font-semibold">Total supply:</span>{" "}
                    {formatPreflightSupply(preflightResult.simulation.token.totalSupplyHuman)}
                  </div>
                )}

                {Array.isArray(preflightResult.simulation.checks) &&
                  preflightResult.simulation.checks.map((check, index) => (
                    <div key={`${check.name}-${index}`}>
                      {check.status === "passed" ? "✅" : "❌"} {check.name}
                    </div>
                  ))}

                {Array.isArray(preflightResult.simulation.warnings) &&
                  preflightResult.simulation.warnings.length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800">
                      <div className="font-semibold">Warnings</div>
                      {preflightResult.simulation.warnings.map((warning, index) => (
                        <div key={`${warning.type || "warning"}-${index}`}>
                          ⚠️ {warning.message}
                        </div>
                      ))}
                    </div>
                  )}

                {preflightResult.simulation.error && (
                  <div className="text-red-600">{preflightResult.simulation.error}</div>
                )}
              </div>
            )}
          </div>
          <button
            onClick={handleDeploy}
            disabled={
              deployLoading ||
              restoreLoading ||
              !isAuthed ||
              !walletAddress ||
              deploymentUsageSummary.limitReached
            }
            title={
              !isAuthed
                ? "Sign in to deploy."
                : !walletAddress
                  ? "Connect wallet to deploy."
                  : ""
            }
            className="inline-flex w-full items-center justify-center rounded-xl bg-[#050713] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(15,23,42,0.22)] transition hover:-translate-y-0.5 hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {!isAuthed
              ? "Connect Wallet in Header to Deploy"
              : !walletAddress
                ? "Connect Wallet in Header to Deploy"
                : deploymentUsageSummary.limitReached
                  ? "Deployment Limit Reached"
                  : deployLoading
                    ? "Working…"
                    : "Deploy Token"}
          </button>

          {statusText && <p className="text-center text-sm">{statusText}</p>}

          {Array.isArray(deployResult) && deployResult.length > 0 && (
            <div className="mt-4 border rounded p-4 bg-gray-50 text-sm space-y-3">
              <div className="font-semibold">Deployment Results</div>

              {deployResult.map((result, idx) => (
                <div
                  key={idx}
                  className="border rounded p-3 bg-white space-y-2"
                >
                  <div>
                    <strong>Network:</strong>{" "}
                    {networks.find((n) => n.key === result.network)?.label ||
                      result.network ||
                      "-"}
                  </div>

                  {extractDeployedAddress(result) && (
                    <div>
                      <strong>Contract:</strong>{" "}
                      {extractDeployedAddress(result)}
                    </div>
                  )}

                  {result.txHash && (
                    <div>
                      <strong>Transaction:</strong> {result.txHash}
                    </div>
                  )}
                  {tokenType === "ERC20" && (
                    <div>
                      <strong>Initial Supply:</strong> {formatSupplyDisplay(erc20InitialSupply || "0")}
                      <span className="ml-2 text-slate-500">
                        (Decimals: {erc20Decimals || 18})
                      </span>
                    </div>
                  )}

                  {result.explorerUrl && (
                    <a
                      href={result.explorerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="block font-semibold text-[#8C6722] underline underline-offset-4 hover:text-[#5F4616]"
                    >
                      View contract on explorer
                    </a>
                  )}

                  {result.explorerTxUrl && (
                    <a
                      href={result.explorerTxUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="block font-semibold text-[#8C6722] underline underline-offset-4 hover:text-[#5F4616]"
                    >
                      View transaction on explorer
                    </a>
                  )}

                  {result.download && (
                    <a
                      href={`${API_BASE_URL}${result.download}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block font-semibold text-[#8C6722] underline underline-offset-4 hover:text-[#5F4616]"
                    >
                      Download deployment bundle
                    </a>
                  )}

                  {extractDeployedAddress(result) && (
                    <button
                      type="button"
                      onClick={() => {
                        const deployedAddress = extractDeployedAddress(result);
                        if (!deployedAddress) return;
                        setSelectedNetwork(result.network || "sepolia");
                        setMintContractAddress(deployedAddress);
                        setMintTokenType(result.tokenType || tokenType);
                        setMintTo(walletAddress || "");
                        setAutoDetectEnabled(true);
                        setMintResult(null);
                        setTokenInfo(null);
                        setTokenInfoError("");
                        setBalanceError("");
                        setUserBalance(null);
                        setMintStatusText(
                          `Token Actions loaded for ${result.network || "selected network"}.`,
                        );
                      }}
                      className="mt-2 w-full rounded border bg-white px-3 py-2 text-sm font-medium hover:bg-gray-50"
                    >
                      Use for Minting
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Deployments */}
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)] space-y-5 md:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold tracking-[-0.03em] text-[#050713]">
                Recent deployments
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-500">
                Latest deployments saved to your workspace with verification status.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={loadRecentDeployments}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#E8D7AD] hover:bg-[#FFF9EA] hover:text-[#8C6722]"
              >
                Refresh
              </button>

              <label className="flex select-none items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
                <input
                  type="checkbox"
                  checked={recentPrivacyMode}
                  onChange={(e) => setRecentPrivacyMode(e.target.checked)}
                />
                Privacy Mode
              </label>
            </div>
          </div>

          {verificationSyncMessage ? (
            <p className="rounded-2xl border border-[#E8D7AD] bg-[#FFF9EA] px-4 py-3 text-sm font-medium text-[#8C6722]">
              {verificationSyncMessage}
            </p>
          ) : null}

          {recentLoading && (
            <p className="text-sm text-slate-600">
              Loading recent deployments…
            </p>
          )}

          {recentError && (
            <p className="text-sm text-slate-600">
              {recentError}{" "}
              <span className="text-slate-500">Use the header to sign in.</span>
            </p>
          )}

          {!recentLoading && !recentError && recentRows.length === 0 && (
            <p className="text-sm text-slate-600">
              No deployments recorded yet.
            </p>
          )}

          {!recentLoading && !recentError && recentRows.length > 0 && (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="bg-[#F8FAFC]">
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Token
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Type
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Supply
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Network
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Contract
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Tx
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Verification
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Bundle
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {recentRows.map((d, idx) => (
                    <tr key={idx} className="transition hover:bg-slate-50">
                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        <div className="flex flex-col gap-1">
                          <span>{d.tokenName}</span>
                          {d.isCustom ? (
                            <span className="w-fit rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              Custom
                            </span>
                          ) : null}
                        </div>
                      </td>

                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        {d.tokenType}
                      </td>

                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        {d.tokenType === "ERC20" ? (
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-900">
                              {formatSupplyDisplay(d.initialSupplyHuman, d.decimals, d.initialSupply)}
                            </span>
                            <span className="text-xs text-slate-500">
                              Decimals: {d.decimals ?? 18}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>

                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        {d.network}
                      </td>

                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        {recentPrivacyMode ? (
                          <span className="text-slate-500">Hidden</span>
                        ) : d.explorerUrl ? (
                          <a
                            href={d.explorerUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                            title={d.address}
                          >
                            {shorten(d.address)}
                          </a>
                        ) : (
                          <span title={d.address}>{shorten(d.address)}</span>
                        )}
                      </td>

                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        {recentPrivacyMode ? (
                          <span className="text-slate-500">Hidden</span>
                        ) : d.explorerTxUrl ? (
                          <a
                            href={d.explorerTxUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline"
                            title={d.txHash}
                          >
                            {shorten(d.txHash)}
                          </a>
                        ) : (
                          <span title={d.txHash}>{shorten(d.txHash)}</span>
                        )}
                      </td>

                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        {(() => {
                          const status = String(d.verificationStatus || "not_requested").toLowerCase();
                          const isVerified = status === "verified";
                          const syncKey =
                            d.txHash || d.requestId || d.deploymentFile || d.customVersionId || d.address;
                          const canSync = Boolean(d.hasVerificationContext);

                          return (
                            <div className="flex flex-col items-start gap-2">
                              <span
                                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${isVerified
                                  ? "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700"
                                  : status === "failed"
                                    ? "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700"
                                    : "border-slate-200 bg-gradient-to-r from-slate-50 to-white text-slate-700"
                                  }`}
                              >
                                {isVerified ? "verified" : status}
                              </span>
                              {!isVerified && canSync ? (
                                <button
                                  type="button"
                                  onClick={() => syncDeploymentVerification(d)}
                                  disabled={Boolean(verificationSyncing[syncKey])}
                                  className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-[11px] font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
                                  title="Re-check explorer verification and update app status"
                                >
                                  {verificationSyncing[syncKey] ? "Syncing…" : "Sync"}
                                </button>
                              ) : !isVerified ? (
                                <span className="text-xs text-slate-400">Missing sync context</span>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>

                      <td className="border-b border-slate-100 px-3 py-3 text-slate-700">
                        {d.download ? (
                          <a
                            href={`${API_BASE_URL}${d.download}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center rounded-full border border-indigo-200 bg-gradient-to-r from-indigo-50 to-sky-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:brightness-95"
                          >
                            Download Bundle
                          </a>
                        ) : d.isCustom ? (
                          <span className="text-slate-500">Custom source managed</span>
                        ) : (
                          <span className="text-slate-400">No bundle</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-2 text-xs ">
                <span>Need full history? </span>
                <Link to="/deployments" className="text-blue-600 underline">
                  Open Deployments
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Import Existing Token */}
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)] space-y-5 md:p-6">
          <div>
            <h2 className="text-xl font-semibold">Import Existing Contract</h2>
            <p className="text-sm text-slate-600">
              Paste a contract address to manage supported operations without creating a new deployment.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Contract intake
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Load an already deployed contract into Krionex operations without creating a new deployment.
            </p>
          </div>

          <select
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
            value={selectedNetwork}
            onChange={(e) => setSelectedNetwork(e.target.value)}
          >
            {networks.map((n) => (
              <option key={n.key} value={n.key}>
                {n.label}
              </option>
            ))}
          </select>

          <input
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
            placeholder="Token contract address (0x...)"
            value={importAddress}
            onChange={(e) => setImportAddress(e.target.value)}
          />

          <button
            onClick={handleImportToken}
            disabled={!isAuthed}
            className="inline-flex w-full items-center justify-center rounded-xl bg-[#050713] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
            title={!isAuthed ? "Sign in to import tokens." : ""}
          >
            Import Token
          </button>

          {importStatus && (
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] px-4 py-3 text-center text-sm font-medium text-slate-700">
              {importStatus}
            </div>
          )}

          {Array.isArray(importedTokens) && importedTokens.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Recently Imported
              </div>

              <div className="space-y-2">
                {importedTokens.slice(0, 5).map((t, idx) => (
                  <button
                    key={idx}
                    className="w-full rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#E8D7AD] hover:bg-[#FFFDF8] hover:shadow-[0_12px_30px_rgba(15,23,42,0.06)]"
                    onClick={() => {
                      setSelectedNetwork(t.network);
                      setImportAddress(t.address);
                      setMintContractAddress(t.address);
                      setDetectedTokenType(t.tokenType);
                      setMintTokenType(t.tokenType);
                      setAutoDetectEnabled(true);
                      setTokenInfo(null);
                      setTokenInfoError("");
                      setImportStatus("Loaded");
                      setMintStatusText("");
                      setMintResult(null);
                    }}
                  >
                    <div className="text-sm font-semibold text-[#050713]">
                      {t.name && t.symbol
                        ? `${t.name} (${t.symbol})`
                        : shorten(t.address)}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {t.tokenType} • {t.network}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Token Actions */}
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)] space-y-5 md:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-[#E8D7AD] bg-[#FFF9EA] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9A7226]">
                Token operations
              </div>

              <h2 className="mt-3 text-2xl font-semibold tracking-[-0.035em] text-[#050713]">
                Token actions
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                Mint tokens by preparing an action and signing the transaction in your wallet.
              </p>

              <p className="mt-1 text-xs leading-5 text-slate-500">
                After a single deployment, Token Actions can be prefilled
                automatically. For multi-network deployments, use the “Use for
                Minting” button from a deployment result above.
              </p>
            </div>

            <label className="flex select-none items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm">
              <input
                type="checkbox"
                checked={autoDetectEnabled}
                onChange={(e) => setAutoDetectEnabled(e.target.checked)}
              />
              Auto-detect token type
            </label>
          </div>

          {autoDetectEnabled && (
            <div className="text-xs">
              {detectStatus === "detecting" && (
                <span className="text-slate-600">Detecting token type…</span>
              )}
              {detectStatus === "ok" && (
                <span className="text-green-700">{detectMessage}</span>
              )}
              {detectStatus === "failed" && (
                <span className="text-amber-700">{detectMessage}</span>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Mint configuration
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Load a deployed contract, choose the token standard, and prepare the next wallet-signed mint action. ERC721 uses Token ID. ERC1155 uses Token ID + Amount. Advanced data is optional.
            </p>
          </div>

          {/* Token Info + Balance */}
          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 shadow-sm space-y-3">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Token Info
            </div>

            {tokenInfoLoading ? (
              <div className="text-sm text-slate-600">Loading token info…</div>
            ) : tokenInfoError ? (
              <div className="text-sm text-slate-600">{tokenInfoError}</div>
            ) : tokenDisplayName ? (
              <div className="text-sm">
                You’re minting: <strong>{tokenDisplayName}</strong>
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                Paste a contract address to load token details.
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="mt-1 text-xs text-slate-500">
                  Your balance{" "}
                  {mintUiTokenType === "ERC1155" ? "(for Token ID)" : ""}
                </div>
                <div className="font-semibold text-sm">
                  {balanceLoading ? "Loading…" : (userBalance ?? "-")}
                </div>
                {balanceError && (
                  <div className="text-xs text-red-600">{balanceError}</div>
                )}
              </div>

              <button
                onClick={refreshUserBalance}
                className="text-sm px-3 py-2 rounded border bg-white hover:bg-gray-50"
              >
                Refresh Balance
              </button>
            </div>
          </div>

          <select
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
            value={mintTokenType}
            onChange={(e) => setMintTokenType(e.target.value)}
            disabled={autoDetectEnabled && Boolean(detectedTokenType)}
            title={
              autoDetectEnabled && detectedTokenType
                ? "Token type is auto-detected. Disable auto-detect to override."
                : ""
            }
          >
            <option value="ERC20">ERC20</option>
            <option value="ERC721">ERC721</option>
            <option value="ERC1155">ERC1155</option>
          </select>

          <input
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
            placeholder="Contract Address (0x...)"
            value={mintContractAddress}
            onChange={(e) => setMintContractAddress(e.target.value)}
          />

          {isEvmAddress(mintContractAddress) && (
            <div className="text-xs mt-2">
              <Link
                to={`/token/${selectedNetwork}/${mintContractAddress.trim()}`}
                className="text-blue-600 hover:underline"
              >
                Open Token Dashboard →
              </Link>
            </div>
          )}

          <input
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
            placeholder="Mint To Address (0x...)"
            value={mintTo}
            onChange={(e) => setMintTo(e.target.value)}
          />

          {mintNeedsTokenId && (
            <div className="space-y-1">
              <label className="text-xs text-slate-600">Token ID</label>
              <input
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                placeholder="Token ID (integer, e.g. 0)"
                value={mintTokenId}
                onChange={(e) => setMintTokenId(e.target.value)}
              />
              {mintUiTokenType === "ERC1155" && (
                <p className="text-xs text-slate-500">
                  Token ID identifies the asset type within the ERC1155 contract.
                </p>
              )}
              {mintUiTokenType === "ERC721" && (
                <p className="text-xs text-slate-500">
                  Token ID is the unique identifier for the NFT you want to mint.
                </p>
              )}
            </div>
          )}

          {mintUiTokenType === "ERC20" && (
            <input
              className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
              placeholder="Amount (e.g. 100.5) — human units"
              value={mintAmount}
              onChange={(e) => setMintAmount(e.target.value)}
            />
          )}

          {mintUiTokenType === "ERC1155" && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs text-slate-600">Amount</label>
                <input
                  className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                  placeholder="Amount (integer, e.g. 1)"
                  value={mintAmount}
                  onChange={(e) => setMintAmount(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  ERC1155 uses Token ID + Amount. Amount must be a whole-number quantity.
                </p>
              </div>

              <details className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <summary className="cursor-pointer text-sm font-medium text-slate-700">
                  Advanced Data (optional)
                </summary>
                <div className="mt-3 space-y-1">
                  <input
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm text-[#050713] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E8D7AD]/45"
                    placeholder="0x"
                    value={mintDataBytes}
                    onChange={(e) => setMintDataBytes(e.target.value)}
                  />
                  <p className="text-xs text-slate-500">
                    Advanced Data is optional and usually stays 0x unless your contract expects extra data.
                  </p>
                </div>
              </details>
            </div>
          )}

          {isMintLocked(mintUiTokenType) ? (
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50 via-white to-sky-50 p-5 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="inline-flex rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs font-semibold text-indigo-700">
                    Pro Feature
                  </div>

                  <h3 className="mt-3 text-base font-semibold text-slate-900">
                    Upgrade to Starter or Pro to unlock minting
                  </h3>

                  <p className="mt-1 max-w-xl text-sm leading-6 text-slate-600">
                    Mint tokens, manage contract actions, and operate deployed assets directly from Krionex.
                  </p>
                </div>

                <Link
                  to="/upgrade"
                  className="inline-flex shrink-0 items-center justify-center rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 hover:shadow-indigo-300 active:scale-[0.98]"
                >
                  Upgrade to Starter or Pro
                </Link>
              </div>
            </div>
          ) : (
            <button
              onClick={handleMint}
              disabled={mintLoading || !isAuthed || !walletAddress}
              className="inline-flex w-full items-center justify-center rounded-xl bg-[#050713] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
              title={
                !isAuthed
                  ? "Sign in to mint."
                  : !walletAddress
                    ? "Connect wallet to mint."
                    : ""
              }
            >
              {mintLoading ? "Working…" : "Mint"}
            </button>
          )}

          {mintStatusText && !mintStatusText.toLowerCase().includes("pro plan") && (
            <p className="text-center text-sm mt-2">{mintStatusText}</p>
          )}

          {mintResult && (
            <div className="mt-3 border rounded p-3 bg-gray-50 text-sm space-y-2">
              <div>
                <strong>Status:</strong> {String(mintResult.ok)}
              </div>
              {mintResult.txHash && (
                <div>
                  <strong>Tx:</strong> {mintResult.txHash}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Activity Log */}
        <div className="border rounded p-4">
          <h2 className="text-xl font-semibold">Activity Log</h2>

          {activityLog.length === 0 ? (
            <p className="text-sm text-slate-500">No activity recorded yet.</p>
          ) : (
            <ul className="text-sm space-y-1">
              {activityLog.map((item, i) => (
                <li key={i}>
                  <span className="text-slate-600">
                    {new Date(item.timestamp).toLocaleString()} —
                  </span>{" "}
                  {item.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------
 * App Router
 * ------------------------------------------------------------------- */

function RouteGateLoader({ text = "Checking access..." }) {
  return (
    <>
      <GlobalNetworkBanner />
      <AppLayout showAdminLink={false}>
        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-700">
            {text}
          </div>
        </div>
      </AppLayout>
    </>
  );
}

function AppShell() {
  return (
    <Router>
      <PaddleCheckoutHandler />
      <AppShellRoutes />
    </Router>
  );
}

function AppShellRoutes() {
  const { isAuthed, restoreLoading, authReady = true } = useAuth();
  const [accountUsage, setAccountUsage] = useState(undefined);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    async function loadAccountUsage() {
      if (!authReady || restoreLoading) {
        return;
      }

      if (!isAuthed) {
        if (mounted) setAccountUsage(null);
        return;
      }

      try {
        const res = await api.get("/account/usage", {
          params: { _ts: Date.now() },
          headers: {
            "Cache-Control": "no-store, no-cache, max-age=0",
            Pragma: "no-cache",
          },
        });

        if (mounted) {
          setAccountUsage(res.data || null);
        }
      } catch {
        if (mounted) {
          setAccountUsage(null);
        }
      }
    }

    loadAccountUsage();

    return () => {
      mounted = false;
    };
  }, [isAuthed, restoreLoading, authReady]);

  const normalizedRole = String(accountUsage?.role || "")
    .trim()
    .toLowerCase();

  const canAccessAdmin =
    ["owner", "admin"].includes(normalizedRole) &&
    Boolean(
      accountUsage?.isPrivileged ||
      accountUsage?.isInternal ||
      accountUsage?.bypassLimits ||
      accountUsage?.isAdmin ||
      normalizedRole === "owner"
    );

  const showAdminLink = canAccessAdmin;

  const planCode = normalizePlanCode(accountUsage?.plan || "free");

  const accessLabel =
    normalizedRole === "owner"
      ? "Owner"
      : normalizedRole === "admin"
        ? "Admin"
        : planCode === "team"
          ? "Team"
          : planCode === "pro"
            ? "Pro"
            : planCode === "starter"
              ? "Starter"
              : "Free";

  const isAdminPath =
    location.pathname === "/admin" || location.pathname === "/admin-dashboard";

  if (isAdminPath && !authReady) {
    return <RouteGateLoader text="Checking session..." />;
  }

  if (isAdminPath && authReady && restoreLoading) {
    return <RouteGateLoader text="Restoring session..." />;
  }

  return (
    <>
      <GlobalNetworkBanner />

      <Routes>
        <Route path="/" element={<Landing />} />

        <Route
          path="/*"
          element={
            <AppLayout showAdminLink={showAdminLink} accessLabel={accessLabel}>
              <Routes>
                <Route path="/studio" element={<Home />} />
                <Route path="/custom-builder" element={<CustomBuilder />} />

                <Route path="/guide" element={<Guide />} />
                <Route path="/about" element={<About />} />
                <Route path="/examples" element={<Examples />} />
                <Route path="/trust" element={<Trust />} />

                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/contact" element={<Contact />} />

                <Route path="/operations" element={<Operations />} />
                <Route path="/dashboard" element={<Navigate to="/operations" replace />} />
                <Route path="/deployments" element={<Deployments />} />
                <Route path="/history" element={<History />} />
                <Route path="/help-center" element={<HelpCenter />} />
                <Route path="/help-center/:ticketId" element={<TicketDetail />} />
                <Route path="/token/:network/:address" element={<TokenDashboard />} />
                <Route path="/upgrade" element={<Upgrade />} />
                <Route path="/account" element={<AccountSettings />} />

                <Route
                  path="/admin"
                  element={canAccessAdmin ? <AdminDashboard /> : <Navigate to="/" replace />}
                />

                <Route
                  path="/admin-dashboard"
                  element={
                    canAccessAdmin ? <Navigate to="/admin" replace /> : <Navigate to="/" replace />
                  }
                />

                <Route path="*" element={<Navigate to="/studio" replace />} />
              </Routes>
            </AppLayout>
          }
        />
      </Routes>
    </>
  );
}
export default function App() {
  return (
    <AuthProvider>
      <NetworkStatusProvider>
        <AppShell />
      </NetworkStatusProvider>
    </AuthProvider>
  );
}
